import { useState, useEffect } from "react";
import {
  collection, doc, setDoc, deleteDoc, onSnapshot
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db, storage } from "./firebase";
import LOGO_B64 from "./logo";

// ─── TEMA MODERNO & IDENTIDADE VISUAL ────────────────────────────────────────
const T = {
  bg: "#F8FAFC",
  surface: "#FFFFFF",
  card: "#FFFFFF",
  border: "#E2E8F0",
  borderLight: "#CBD5E1",
  accent: "#15803D",        // Verde nobre esmeralda da consultoria
  accentHover: "#166534",
  accentDim: "#DCFCE7",
  accentText: "#166534",
  text: "#0F172A",
  muted: "#64748B",
  mutedLight: "#475569",
  danger: "#EF4444",
  dangerDim: "#FEE2E2",
  gold: "#D97706",
  goldDim: "#FEF3C7",
  blue: "#2563EB",
  blueDim: "#DBEAFE",
  purple: "#7C3AED",
  purpleDim: "#F3E8FF",
  whatsapp: "#25D366",
};

const CATEGORIAS_DOCS = [
  { id: "Contratos & Matrículas", icon: "📜" },
  { id: "Regimento & Pedagógico", icon: "🏫" },
  { id: "Pareceres Jurídicos", icon: "⚖️" },
  { id: "LGPD & Proteção de Dados", icon: "🛡️" },
  { id: "Inadimplência & Cobrança", icon: "⚠️" },
  { id: "Trabalhista & Docentes", icon: "👥" },
  { id: "Segurança & Conflitos Escolares", icon: "🚨" },
  { id: "Modelos, Minutas & Circulares", icon: "📝" },
  { id: "Administrativo & Gestão", icon: "🏢" },
  { id: "Outros", icon: "📁" },
];

const CATS = CATEGORIAS_DOCS.map(c => c.id);

function nowBR() { return new Date().toLocaleDateString("pt-BR"); }
function nowBRFull() {
  const d = new Date();
  return `${d.toLocaleDateString("pt-BR")} às ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
}
function uid() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 6); }

function deadlineStatus(dateStr) {
  if (!dateStr) return "ok";
  const [d, m, y] = dateStr.split("/");
  const dt = new Date(+y, +m - 1, +d);
  const diff = Math.ceil((dt - new Date()) / 86400000);
  if (diff < 0) return "late";
  if (diff <= 3) return "warn";
  return "ok";
}

function shareOnWhatsApp(titulo, categoria, link, extra = "", phone = "") {
  const text = encodeURIComponent(
    `*Portal Vieira & Melul — Consultoria Jurídica Educacional*\n\n` +
    `📌 *${titulo}*\n` +
    (categoria ? `🏷️ *Categoria:* ${categoria}\n` : "") +
    (extra ? `📝 *Resumo/Orientação:* ${extra}\n` : "") +
    (link ? `🔗 *Acesse o arquivo:* ${link}\n\n` : `\n`) +
    `_Acesse o portal para consultar e registrar a ciência institucional._`
  );
  const cleanPhone = (phone || "").replace(/\D/g, "");
  const url = cleanPhone
    ? `https://api.whatsapp.com/send?phone=55${cleanPhone}&text=${text}`
    : `https://api.whatsapp.com/send?text=${text}`;
  window.open(url, "_blank");
}

function exportReportCSV(schools, docs, tasks) {
  let csv = "\uFEFFNome do Colégio;Login;Responsável;WhatsApp;Documentos Vigentes;Total Documentos;Pendências Abertas;Pendências Concluídas;Data de Cadastro\n";
  schools.forEach(s => {
    const docVigentes = docs.filter(d => (d.schoolId === s.id || d.schoolId === "all") && d.status === "Vigente").length;
    const totalDocs = docs.filter(d => d.schoolId === s.id || d.schoolId === "all").length;
    const pendAbertas = tasks.filter(t => t.schoolId === s.id && (t.status === "pending" || t.status === "overdue")).length;
    const pendConcluidas = tasks.filter(t => t.schoolId === s.id && t.status === "approved").length;
    csv += `"${s.name}";"${s.login}";"${s.responsavel || ""}";"${s.telefone || ""}";${docVigentes};${totalDocs};${pendAbertas};${pendConcluidas};"${s.dataCadastro || ""}"\n`;
  });
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.setAttribute("download", `relatorio_colegios_vieira_melul_${nowBR().replace(/\//g, "-")}.csv`);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

// ─── CSS MODERNO E FLUIDO ───────────────────────────────────────────────────
const css = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Syne:wght@600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');

* { box-sizing: border-box; margin: 0; padding: 0; -webkit-font-smoothing: antialiased; }
body { background: ${T.bg}; color: ${T.text}; font-family: 'Plus Jakarta Sans', sans-serif; min-height: 100vh; }

::-webkit-scrollbar { width: 6px; height: 6px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 4px; }
::-webkit-scrollbar-thumb:hover { background: #94A3B8; }

.wrap { min-height: 100vh; display: flex; flex-direction: column; }

/* Login */
.login-outer {
  min-height: 100vh; display: flex; align-items: center; justify-content: center;
  background: radial-gradient(circle at 10% 20%, rgba(220, 252, 231, 0.7) 0%, transparent 45%),
              radial-gradient(circle at 90% 80%, rgba(219, 234, 254, 0.7) 0%, transparent 45%),
              #F8FAFC;
  position: relative; overflow: hidden; padding: 24px;
}
.login-box {
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(226, 232, 240, 0.9);
  border-radius: 20px;
  padding: 40px 36px;
  width: 100%;
  max-width: 480px;
  position: relative;
  z-index: 10;
  box-shadow: 0 20px 40px -15px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(226, 232, 240, 0.6);
  transition: all 0.3s ease;
}
.login-title { font-family: 'Syne', sans-serif; font-size: 24px; font-weight: 800; color: #0F172A; margin-bottom: 4px; }
.login-sub { font-size: 13px; color: ${T.muted}; margin-bottom: 24px; }

.login-tabs { display: flex; background: #F1F5F9; border-radius: 12px; padding: 4px; margin-bottom: 24px; gap: 4px; }
.login-tab {
  flex: 1; text-align: center; padding: 10px; font-size: 13px; font-weight: 600;
  color: ${T.muted}; cursor: pointer; border-radius: 8px; transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}
.login-tab.active { background: #FFFFFF; color: ${T.accent}; box-shadow: 0 2px 8px rgba(0,0,0,0.06); }

.field-label { font-size: 12px; font-weight: 600; color: #334155; display: block; margin-bottom: 6px; }
.field-input {
  width: 100%; padding: 12px 16px; background: #FFFFFF; border: 1.5px solid #E2E8F0;
  border-radius: 10px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; color: ${T.text};
  outline: none; transition: all 0.2s ease; margin-bottom: 16px;
}
.field-input:focus { border-color: ${T.accent}; box-shadow: 0 0 0 4px ${T.accentDim}; }

.login-btn {
  width: 100%; padding: 13px; background: linear-gradient(135deg, #15803D 0%, #166534 100%);
  color: #FFFFFF; border: none; font-size: 14px; font-weight: 700; border-radius: 10px;
  cursor: pointer; transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1); box-shadow: 0 4px 14px rgba(22, 101, 52, 0.25);
}
.login-btn:hover { background: #14532D; transform: translateY(-1px); box-shadow: 0 6px 18px rgba(22, 101, 52, 0.35); }
.login-btn:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }

.login-link {
  background: none; border: none; color: ${T.accent}; cursor: pointer; font-size: 13px; font-weight: 600;
  transition: all 0.2s ease; display: inline-flex; align-items: center; gap: 4px;
}
.login-link:hover { color: #14532D; text-decoration: underline; }
.login-err { font-size: 13px; color: ${T.danger}; margin-top: 14px; padding: 10px 14px; background: ${T.dangerDim}; border-radius: 8px; text-align: center; }
.login-success { font-size: 13px; color: ${T.accent}; margin-top: 14px; padding: 10px 14px; background: ${T.accentDim}; border-radius: 8px; text-align: center; font-weight: 600; }
.login-hint { font-size: 12px; color: ${T.muted}; margin-top: 24px; text-align: center; line-height: 1.6; border-top: 1px solid #E2E8F0; padding-top: 20px; }

/* Header */
.header {
  height: 74px; background: #FFFFFF; border-bottom: 1px solid #E2E8F0;
  display: flex; align-items: center; justify-content: space-between; padding: 0 32px;
  position: sticky; top: 0; z-index: 100; box-shadow: 0 1px 4px rgba(0,0,0,0.03);
}
.header-brand { display: flex; align-items: center; gap: 14px; cursor: pointer; }
.header-r { display: flex; align-items: center; gap: 16px; }
.user-badge {
  display: flex; align-items: center; gap: 10px; background: #F8FAFC; border: 1px solid #E2E8F0;
  border-radius: 30px; padding: 6px 14px 6px 8px; cursor: pointer; transition: all 0.2s ease;
}
.user-badge:hover { background: #F1F5F9; border-color: #CBD5E1; }
.user-avatar {
  width: 32px; height: 32px; border-radius: 50%; background: ${T.accentDim}; color: ${T.accentText};
  display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 13px;
}
.user-info { display: flex; flex-direction: column; }
.user-name { font-size: 13px; font-weight: 700; color: #1E293B; line-height: 1.2; }
.user-role { font-size: 11px; color: ${T.muted}; }
.logout-btn {
  background: transparent; border: 1.5px solid #E2E8F0; color: #64748B; font-size: 13px; font-weight: 600;
  padding: 8px 16px; cursor: pointer; border-radius: 10px; transition: all 0.2s ease;
}
.logout-btn:hover { border-color: ${T.danger}; color: ${T.danger}; background: ${T.dangerDim}; }

/* Layout Body & Sidebar */
.app-body { display: flex; flex: 1; overflow: hidden; min-height: calc(100vh - 74px); }
.sidebar {
  width: 260px; background: #FFFFFF; border-right: 1px solid #E2E8F0;
  padding: 24px 16px; flex-shrink: 0; display: flex; flex-direction: column; gap: 4px;
}
.nav-section-title {
  font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
  color: #94A3B8; padding: 14px 12px 6px;
}
.nav-item {
  display: flex; align-items: center; gap: 12px; padding: 12px 14px; font-size: 14px;
  font-weight: 600; color: #475569; cursor: pointer; transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  border-radius: 10px;
}
.nav-item:hover { background: #F1F5F9; color: #0F172A; }
.nav-item.active { background: ${T.accentDim}; color: ${T.accentText}; font-weight: 700; }
.nav-badge {
  margin-left: auto; background: ${T.danger}; color: #fff; font-size: 11px;
  padding: 2px 8px; border-radius: 12px; font-weight: 700;
}
.nav-badge-gold { background: ${T.gold}; color: #fff; }

/* Main Content */
.main-content { flex: 1; overflow-y: auto; padding: 36px 48px; background: ${T.bg}; }
.page-header-row {
  display: flex; align-items: flex-start; justify-content: space-between; gap: 20px;
  margin-bottom: 28px; flex-wrap: wrap;
}
.page-title { font-family: 'Syne', sans-serif; font-size: 26px; font-weight: 800; color: #0F172A; margin-bottom: 4px; }
.page-sub { font-size: 14px; color: ${T.muted}; }

/* Buttons */
.primary-btn {
  display: inline-flex; align-items: center; gap: 8px; padding: 12px 22px;
  background: linear-gradient(135deg, #15803D 0%, #166534 100%); color: #FFFFFF;
  border: none; font-size: 13px; font-weight: 700; border-radius: 10px; cursor: pointer;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1); box-shadow: 0 4px 12px rgba(22, 101, 52, 0.2);
}
.primary-btn:hover { background: #14532D; transform: translateY(-2px); box-shadow: 0 6px 18px rgba(22, 101, 52, 0.3); }
.primary-btn:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }

.secondary-btn {
  display: inline-flex; align-items: center; gap: 8px; padding: 11px 20px;
  background: #FFFFFF; color: #334155; border: 1.5px solid #E2E8F0;
  font-size: 13px; font-weight: 700; border-radius: 10px; cursor: pointer;
  transition: all 0.2s ease;
}
.secondary-btn:hover { border-color: ${T.accent}; color: ${T.accent}; background: #F8FAFC; }

.whatsapp-btn {
  display: inline-flex; align-items: center; gap: 6px; padding: 7px 12px;
  background: #25D366; color: #FFFFFF; border: none; font-size: 12px; font-weight: 700;
  border-radius: 8px; cursor: pointer; transition: all 0.2s ease; text-decoration: none;
}
.whatsapp-btn:hover { background: #1EBE5D; transform: translateY(-1px); }

/* Dashboard Stats */
.stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 16px; margin-bottom: 28px; }
.stat-card {
  background: #FFFFFF; border: 1px solid #E2E8F0; border-radius: 14px; padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02); transition: all 0.2s ease;
}
.stat-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(0,0,0,0.04); }
.stat-number { font-family: 'Syne', sans-serif; font-size: 28px; font-weight: 800; color: #0F172A; line-height: 1.1; }
.stat-label { font-size: 12px; font-weight: 600; color: ${T.muted}; margin-top: 6px; }

/* Search & Filters */
.search-box { position: relative; margin-bottom: 20px; }
.search-input {
  width: 100%; padding: 14px 18px 14px 44px; background: #FFFFFF; border: 1.5px solid #E2E8F0;
  border-radius: 12px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; color: ${T.text};
  outline: none; transition: all 0.2s ease; box-shadow: 0 2px 6px rgba(0,0,0,0.02);
}
.search-input:focus { border-color: ${T.accent}; box-shadow: 0 0 0 4px ${T.accentDim}; }
.search-icon-svg { position: absolute; left: 16px; top: 50%; transform: translateY(-50%); color: #94A3B8; }

.filter-chips { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 24px; }
.chip {
  padding: 8px 16px; border: 1.5px solid #E2E8F0; border-radius: 30px; font-size: 13px; font-weight: 600;
  color: #64748B; cursor: pointer; transition: all 0.15s ease; background: #FFFFFF;
}
.chip:hover { border-color: #CBD5E1; color: #1E293B; }
.chip.active { background: ${T.accent}; border-color: ${T.accent}; color: #FFFFFF; box-shadow: 0 3px 10px rgba(21, 128, 61, 0.25); }

/* Document Cards */
.cards-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(330px, 1fr)); gap: 20px; }
.doc-card {
  background: #FFFFFF; border: 1px solid #E2E8F0; border-radius: 16px; padding: 24px;
  display: flex; flex-direction: column; justify-content: space-between;
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1); box-shadow: 0 2px 10px rgba(0,0,0,0.03);
}
.doc-card:hover {
  transform: translateY(-3px); border-color: #CBD5E1; box-shadow: 0 12px 28px rgba(0, 0, 0, 0.06);
}
.doc-category-badge {
  display: inline-flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.05em; color: ${T.accentText}; background: ${T.accentDim};
  padding: 4px 10px; border-radius: 6px; width: fit-content; margin-bottom: 12px;
}
.doc-heading { font-family: 'Syne', sans-serif; font-size: 16px; font-weight: 700; color: #0F172A; margin-bottom: 8px; line-height: 1.4; }
.doc-description { font-size: 13px; color: ${T.muted}; line-height: 1.6; margin-bottom: 18px; flex-grow: 1; }
.doc-footer-meta {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  padding-top: 14px; border-top: 1px solid #F1F5F9; margin-bottom: 14px;
}
.doc-date-text { font-size: 12px; color: #94A3B8; font-family: 'JetBrains Mono', monospace; }
.badge-pill { font-size: 11px; font-weight: 700; padding: 4px 10px; border-radius: 20px; text-transform: uppercase; }
.badge-pill-green { background: ${T.accentDim}; color: ${T.accentText}; }
.badge-pill-gold { background: ${T.goldDim}; color: #B45309; }
.badge-pill-blue { background: ${T.blueDim}; color: ${T.blue}; }
.badge-pill-red { background: ${T.dangerDim}; color: ${T.danger}; }

.doc-actions-row { display: flex; gap: 8px; flex-wrap: wrap; }
.action-btn {
  flex: 1; min-width: 90px; padding: 9px 12px; border: 1.5px solid #E2E8F0; background: #FFFFFF;
  color: #334155; font-size: 12px; font-weight: 700; cursor: pointer; border-radius: 8px;
  transition: all 0.2s ease; text-align: center; text-decoration: none; display: flex; align-items: center; justify-content: center; gap: 6px;
}
.action-btn:hover { border-color: ${T.accent}; color: ${T.accent}; background: #F8FAFC; }
.action-btn-danger:hover { border-color: ${T.danger}; color: ${T.danger}; background: ${T.dangerDim}; }

/* Ciência Badge */
.ciencia-tag {
  display: inline-flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 700;
  color: #15803D; background: #DCFCE7; padding: 4px 10px; border-radius: 6px; margin-bottom: 10px;
}

/* Modal Preview */
.modal-overlay {
  position: fixed; inset: 0; background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(6px); display: flex; align-items: center; justify-content: center;
  z-index: 1000; padding: 20px; animation: fadeIn 0.2s ease;
}
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
.modal-card {
  background: #FFFFFF; border-radius: 20px; width: 100%; max-width: 760px;
  max-height: 90vh; overflow-y: auto; padding: 32px; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  position: relative; animation: slideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}
@keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
.modal-close {
  position: absolute; right: 24px; top: 24px; background: #F1F5F9; border: none;
  width: 36px; height: 36px; border-radius: 50%; font-size: 16px; cursor: pointer;
  display: flex; align-items: center; justify-content: center; color: #64748B; transition: all 0.2s ease;
}
.modal-close:hover { background: #E2E8F0; color: #0F172A; }

/* Forms Modern */
.form-panel {
  background: #FFFFFF; border: 1px solid #E2E8F0; border-radius: 16px; padding: 36px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.03); max-width: 800px; margin-bottom: 30px;
}
.form-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
.form-group-item { display: flex; flex-direction: column; margin-bottom: 20px; }
.form-control-input {
  padding: 12px 16px; background: #F8FAFC; border: 1.5px solid #E2E8F0; border-radius: 10px;
  font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; color: ${T.text}; outline: none; transition: all 0.2s ease;
}
.form-control-input:focus { background: #FFFFFF; border-color: ${T.accent}; box-shadow: 0 0 0 4px ${T.accentDim}; }
textarea.form-control-input { resize: vertical; min-height: 100px; line-height: 1.6; }

/* Upload Box */
.upload-dropzone {
  border: 2px dashed #CBD5E1; border-radius: 12px; padding: 24px; text-align: center;
  background: #F8FAFC; cursor: pointer; transition: all 0.2s ease; margin-bottom: 16px;
}
.upload-dropzone:hover { border-color: ${T.accent}; background: #F0FDF4; }
.upload-progress-bar {
  width: 100%; height: 8px; background: #E2E8F0; border-radius: 4px; overflow: hidden; margin-top: 8px;
}
.upload-progress-fill { height: 100%; background: ${T.accent}; transition: width 0.3s ease; }

/* Empty States */
.empty-box {
  text-align: center; padding: 64px 24px; background: #FFFFFF; border: 2px dashed #E2E8F0;
  border-radius: 16px; color: ${T.muted};
}
.empty-icon-lg { font-size: 48px; margin-bottom: 16px; }
.empty-title { font-family: 'Syne', sans-serif; font-size: 18px; font-weight: 700; color: #1E293B; margin-bottom: 6px; }
.empty-desc { font-size: 14px; color: #64748B; max-width: 440px; margin: 0 auto 20px; line-height: 1.5; }

/* Spinners */
.spinner-circle {
  width: 36px; height: 36px; border: 3px solid ${T.accentDim}; border-top-color: ${T.accent};
  border-radius: 50%; animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* Executive Welcome Banner & Quick Stats */
.welcome-banner {
  background: linear-gradient(135deg, #14532D 0%, #166534 60%, #15803D 100%);
  color: #FFFFFF; border-radius: 16px; padding: 24px 28px; margin-bottom: 24px;
  display: flex; justify-content: space-between; align-items: center; gap: 20px;
  box-shadow: 0 10px 25px -5px rgba(20, 83, 45, 0.25); position: relative; overflow: hidden;
}
.welcome-banner::after {
  content: ""; position: absolute; right: -20px; bottom: -20px; width: 140px; height: 140px;
  background: radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%); border-radius: 50%;
}
.welcome-banner-title { font-family: 'Syne', sans-serif; font-size: 20px; font-weight: 800; margin-bottom: 4px; }
.welcome-banner-sub { font-size: 13px; color: #DCFCE7; opacity: 0.9; }

.quick-stats-row { display: flex; gap: 12px; flex-wrap: wrap; }
.quick-stat-pill {
  background: rgba(255, 255, 255, 0.15); backdrop-filter: blur(8px); border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 10px; padding: 8px 16px; display: flex; align-items: center; gap: 10px; font-size: 13px; font-weight: 600;
}
.quick-stat-pill-num { font-size: 16px; font-weight: 800; font-family: 'Syne', sans-serif; }

/* Floating WhatsApp Button */
.floating-whatsapp-btn {
  position: fixed; bottom: 28px; right: 28px; z-index: 999;
  background: #25D366; color: #FFFFFF; text-decoration: none;
  padding: 12px 20px; border-radius: 40px; display: flex; align-items: center; gap: 10px;
  font-size: 13px; font-weight: 700; box-shadow: 0 8px 24px rgba(37, 211, 102, 0.4);
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.floating-whatsapp-btn:hover { background: #1EBE5D; transform: translateY(-3px) scale(1.02); box-shadow: 0 12px 28px rgba(37, 211, 102, 0.5); }

/* Printable Protocol */
.printable-protocol {
  background: #FFFFFF; border: 2px solid #E2E8F0; border-radius: 12px; padding: 32px;
  font-family: 'Plus Jakarta Sans', sans-serif; color: #0F172A; max-width: 650px; margin: 0 auto;
}
@media print {
  body * { visibility: hidden !important; }
  .printable-protocol, .printable-protocol * { visibility: visible !important; }
  .printable-protocol { position: absolute !important; left: 0 !important; top: 0 !important; width: 100% !important; border: none !important; box-shadow: none !important; }
  .no-print { display: none !important; }
}
`;

// ─── COMPONENTE PRINCIPAL (APP) ──────────────────────────────────────────────
export default function App() {
  const [user, setUser]       = useState(null);
  const [schools, setSchools] = useState([]);
  const [admins, setAdmins]   = useState([]);
  const [docs, setDocs]       = useState([]);
  const [tasks, setTasks]     = useState([]);
  const [comms, setComms]     = useState([]);
  const [readComms, setReadComms] = useState(() => {
    try { return JSON.parse(localStorage.getItem("readComms") || "[]"); } catch { return []; }
  });
  const [loading, setLoading] = useState(true);

  // Carregamento Firestore resiliente
  useEffect(() => {
    let loaded = false;
    const finish = () => { if (!loaded) { loaded = true; setLoading(false); } };

    const unsubs = [
      onSnapshot(collection(db, "schools"), snap => {
        setSchools(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        finish();
      }, finish),
      onSnapshot(collection(db, "admins"), snap => setAdmins(snap.docs.map(d => ({ id: d.id, ...d.data() }))), finish),
      onSnapshot(collection(db, "docs"), snap => setDocs(snap.docs.map(d => ({ id: d.id, ...d.data() }))), finish),
      onSnapshot(collection(db, "tasks"), snap => setTasks(snap.docs.map(d => ({ id: d.id, ...d.data() }))), finish),
      onSnapshot(collection(db, "comms"), snap => setComms(snap.docs.map(d => ({ id: d.id, ...d.data() }))), finish),
    ];

    const timer = setTimeout(finish, 1200);
    return () => { unsubs.forEach(u => u()); clearTimeout(timer); };
  }, []);

  // Operações de Banco
  const addDoc = async (d) => { await setDoc(doc(db, "docs", d.id), d); };
  const updateDocData = async (id, patch) => {
    const d = docs.find(item => item.id === id);
    if (d) await setDoc(doc(db, "docs", id), { ...d, ...patch });
  };
  const delDoc = async (id) => { await deleteDoc(doc(db, "docs", id)); };

  const addTask    = async (t) => { await setDoc(doc(db, "tasks", t.id), t); };
  const delTask    = async (id) => { await deleteDoc(doc(db, "tasks", id)); };
  const updateTask = async (id, patch) => {
    const t = tasks.find(item => item.id === id);
    if (t) await setDoc(doc(db, "tasks", id), { ...t, ...patch });
  };

  const addComm    = async (c) => { await setDoc(doc(db, "comms", c.id), c); };
  const delComm    = async (id) => { await deleteDoc(doc(db, "comms", id)); };

  const addSchool  = async (s) => { await setDoc(doc(db, "schools", s.id), s); };
  const addAdmin   = async (a) => { await setDoc(doc(db, "admins", a.id), a); };
  const delAdmin   = async (id) => { await deleteDoc(doc(db, "admins", id)); };
  const updateSchool = async (id, patch) => {
    const s = schools.find(item => item.id === id);
    if (s) {
      const updated = { ...s, ...patch };
      await setDoc(doc(db, "schools", id), updated);
      if (user && user.id === id) setUser(updated);
    }
  };
  const delSchool  = async (id) => { await deleteDoc(doc(db, "schools", id)); };

  const markRead = (id) => {
    const next = [...new Set([...readComms, id])];
    setReadComms(next);
    localStorage.setItem("readComms", JSON.stringify(next));
  };

  if (loading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: T.bg }}>
        <style>{css}</style>
        <img src={LOGO_B64} alt="Vieira & Melul" style={{ height: 80, objectFit: "contain", marginBottom: 24 }} />
        <div className="spinner-circle" style={{ marginBottom: 16 }} />
        <div style={{ fontSize: 14, fontWeight: 700, color: T.accent }}>Carregando Portal Vieira &amp; Melul…</div>
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <style>{css}</style>
        <AuthScreen schools={schools} admins={admins} onLogin={setUser} onRegister={addSchool} onRegisterAdmin={addAdmin} />
      </>
    );
  }

  // Filtragem estrita por perfil:
  // Admin vê tudo; Colégio vê apenas os seus ou os globais ("all")
  const myDocs  = user.role === "admin" ? docs : docs.filter(d => d.schoolId === user.id || d.schoolId === "all");
  const myTasks = user.role === "admin" ? tasks : tasks.filter(t => t.schoolId === user.id);
  const myComms = user.role === "admin" ? comms : comms.filter(c => c.schoolId === user.id || c.schoolId === "all");

  const unreadCount  = myComms.filter(c => !readComms.includes(c.id)).length;
  const pendingCount = myTasks.filter(t => t.status === "pending" || t.status === "overdue").length;

  return (
    <>
      <style>{css}</style>
      <div className="wrap">
        <Header user={user} onLogout={() => setUser(null)} />
        <div className="app-body">
          <Sidebar user={user} unreadComms={unreadCount} pendingTasks={pendingCount} />
          <MainRouter
            user={user} schools={schools} admins={admins} docs={myDocs} allDocs={docs} tasks={myTasks} allTasks={tasks} comms={myComms}
            readComms={readComms} markRead={markRead}
            addDoc={addDoc} updateDocData={updateDocData} delDoc={delDoc}
            addTask={addTask} delTask={delTask} updateTask={updateTask}
            addComm={addComm} delComm={delComm}
            addSchool={addSchool} updateSchool={updateSchool} delSchool={delSchool}
            delAdmin={delAdmin}
          />
        </div>
      </div>

      {/* Botão Flutuante de Plantão Jurídico (Para os Colégios) */}
      {user.role === "client" && (
        <a
          href={`https://api.whatsapp.com/send?phone=5511999999999&text=${encodeURIComponent(`Olá Dra. Hellen! Sou do ${user.name} e gostaria de tirar uma dúvida sobre a consultoria jurídica educacional.`)}`}
          target="_blank"
          rel="noreferrer"
          className="floating-whatsapp-btn no-print"
          title="Falar com o Plantão Jurídico Vieira & Melul"
        >
          <span style={{ fontSize: 18 }}>💬</span>
          <span>Plantão Dra. Hellen</span>
        </a>
      )}
    </>
  );
}

// ─── TELA DE AUTENTICAÇÃO (LOGIN, CADASTRO COLÉGIO & NOVO ADMIN) ─────────────
function AuthScreen({ schools = [], admins = [], onLogin, onRegister, onRegisterAdmin }) {
  const [tab, setTab] = useState("login"); // "login" | "register" | "admin"
  const [login, setLogin] = useState("");
  const [senha, setSenha] = useState("");

  // Cadastro de Colégio
  const [regName, setRegName] = useState("");
  const [regResp, setRegResp] = useState("");
  const [regPhone, setRegPhone] = useState("");
  const [regLogin, setRegLogin] = useState("");
  const [regSenha, setRegSenha] = useState("");
  const [regSenhaConf, setRegSenhaConf] = useState("");

  // Cadastro de Administrador (Equipe Escritório)
  const [admName, setAdmName] = useState("");
  const [admCargo, setAdmCargo] = useState("");
  const [admLogin, setAdmLogin] = useState("");
  const [admSenha, setAdmSenha] = useState("");
  const [admSenhaConf, setAdmSenhaConf] = useState("");
  const [admPin, setAdmPin] = useState("");

  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = () => {
    const l = login.trim().toLowerCase();
    if (!l || !senha) {
      setErr("Preencha o usuário e a senha.");
      return;
    }
    setLoading(true); setErr("");

    // Acesso Administrador Padrão do Escritório (Hellen Vieira / Escritório)
    if ((l === "hellen" && (senha === "admin123" || senha === "MelhorSelecao123")) ||
        (l === "admin" && (senha === "MelhorSelecao123" || senha === "admin123"))) {
      onLogin({
        id: "__admin_hellen__",
        name: "Hellen Vieira (Escritório)",
        cargo: "Sócia Diretora",
        login: l,
        role: "admin",
      });
      return;
    }

    // Acesso Administradores Adicionais cadastrados
    const foundAdmin = (admins || []).find(
      a => (a.login || "").toLowerCase() === l && a.senha === senha
    );
    if (foundAdmin) {
      onLogin({
        ...foundAdmin,
        name: foundAdmin.name || "Administrador(a)",
        role: "admin",
      });
      return;
    }

    // Acesso Usuário Colégio / Escola Parceira no Firestore
    const school = (schools || []).find(
      s => (s.login || "").toLowerCase() === l && s.senha === senha
    );
    if (school) {
      onLogin({ ...school, role: "client" });
      return;
    }

    setErr("Usuário ou senha incorretos.");
    setLoading(false);
  };

  const handleRegister = async () => {
    const cleanName  = regName.trim();
    const cleanLogin = regLogin.trim().toLowerCase().replace(/\s+/g, "");

    if (!cleanName || !cleanLogin || !regSenha) {
      setErr("Preencha todos os campos obrigatórios (*).");
      return;
    }
    if (cleanLogin === "admin" || cleanLogin === "hellen") {
      setErr("Este usuário é reservado para a administração do escritório.");
      return;
    }
    if (regSenha.length < 4) {
      setErr("A senha deve possuir pelo menos 4 caracteres.");
      return;
    }
    if (regSenha !== regSenhaConf) {
      setErr("As senhas não coincidem.");
      return;
    }

    const existsSchool = (schools || []).some(s => (s.login || "").toLowerCase() === cleanLogin);
    const existsAdmin  = (admins || []).some(a => (a.login || "").toLowerCase() === cleanLogin);
    if (existsSchool || existsAdmin) {
      setErr("Este usuário já está cadastrado. Por favor, escolha outro.");
      return;
    }

    setLoading(true); setErr("");

    try {
      const newSchool = {
        id: uid(),
        name: cleanName,
        responsavel: regResp.trim() || "",
        telefone: regPhone.trim() || "",
        login: cleanLogin,
        senha: regSenha,
        dataCadastro: nowBR(),
      };

      await onRegister(newSchool);
      setSuccess("Cadastro realizado com sucesso! Entrando no portal…");

      setTimeout(() => {
        onLogin({ ...newSchool, role: "client" });
      }, 1000);
    } catch (error) {
      console.error(error);
      setErr("Erro ao salvar no banco de dados. Verifique a conexão com o Firebase.");
      setLoading(false);
    }
  };

  const handleRegisterAdmin = async () => {
    const cleanName  = admName.trim();
    const cleanLogin = admLogin.trim().toLowerCase().replace(/\s+/g, "");
    const cleanPin   = admPin.trim().toUpperCase();

    if (!cleanName || !cleanLogin || !admSenha || !cleanPin) {
      setErr("Preencha todos os campos obrigatórios (*), incluindo a chave do escritório.");
      return;
    }

    if (cleanPin !== "VIEIRA2026") {
      setErr("Chave de segurança do escritório inválida. Apenas membros autorizados da Vieira & Melul podem criar administradores.");
      return;
    }

    if (cleanLogin === "admin" || cleanLogin === "hellen") {
      setErr("Os usuários 'hellen' e 'admin' já existem! Você pode entrar diretamente na aba de acesso.");
      return;
    }

    if (admSenha.length < 4) {
      setErr("A senha deve possuir pelo menos 4 caracteres.");
      return;
    }

    if (admSenha !== admSenhaConf) {
      setErr("As senhas informadas não coincidem.");
      return;
    }

    const existsSchool = (schools || []).some(s => (s.login || "").toLowerCase() === cleanLogin);
    const existsAdmin  = (admins || []).some(a => (a.login || "").toLowerCase() === cleanLogin);
    if (existsSchool || existsAdmin) {
      setErr("Este usuário de login já está em uso. Por favor, escolha outro.");
      return;
    }

    setLoading(true); setErr("");

    try {
      const newAdmin = {
        id: uid(),
        name: cleanName,
        cargo: admCargo.trim() || "Advogado(a) / Escritório",
        login: cleanLogin,
        senha: admSenha,
        role: "admin",
        dataCadastro: nowBR(),
      };

      if (onRegisterAdmin) {
        await onRegisterAdmin(newAdmin);
      }
      setSuccess("Acesso de Administrador criado com sucesso! Entrando no portal…");

      setTimeout(() => {
        onLogin(newAdmin);
      }, 1000);
    } catch (error) {
      console.error(error);
      setErr("Erro ao salvar administrador no banco de dados.");
      setLoading(false);
    }
  };

  return (
    <div className="login-outer">
      <div className="login-box">
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <img src={LOGO_B64} alt="Vieira & Melul" style={{ height: 110, objectFit: "contain" }} />
        </div>
        <div className="login-title" style={{ textAlign: "center" }}>Portal Documental</div>
        <div className="login-sub" style={{ textAlign: "center" }}>Consultoria Jurídica Educacional — Vieira &amp; Melul</div>

        <div className="login-tabs">
          <div className={`login-tab ${tab === "login" ? "active" : ""}`} onClick={() => { setTab("login"); setErr(""); setSuccess(""); }}>
            Acesso ao Portal
          </div>
          <div className={`login-tab ${tab === "register" ? "active" : ""}`} onClick={() => { setTab("register"); setErr(""); setSuccess(""); }}>
            Cadastrar Colégio
          </div>
          <div className={`login-tab ${tab === "admin" ? "active" : ""}`} onClick={() => { setTab("admin"); setErr(""); setSuccess(""); }}>
            Criar Admin
          </div>
        </div>

        {tab === "login" && (
          <div>
            <label className="field-label">Usuário de Acesso</label>
            <input
              className="field-input"
              value={login}
              onChange={e => setLogin(e.target.value)}
              placeholder="Ex: hellen, admin ou login do colégio"
              onKeyDown={e => e.key === "Enter" && handleLogin()}
              autoFocus
            />
            <label className="field-label">Senha</label>
            <input
              className="field-input"
              type="password"
              value={senha}
              onChange={e => setSenha(e.target.value)}
              placeholder="••••••••"
              onKeyDown={e => e.key === "Enter" && handleLogin()}
            />
            <button className="login-btn" onClick={handleLogin} disabled={loading}>
              {loading ? "Aguarde…" : "Entrar no Portal"}
            </button>
            <div style={{ textAlign: "center", marginTop: 18, display: "flex", flexDirection: "column", gap: 8 }}>
              <button type="button" className="login-link" onClick={() => { setTab("register"); setErr(""); setSuccess(""); }}>
                🏫 Novo colégio? Faça o cadastro da sua instituição →
              </button>
              <button type="button" className="login-link" style={{ fontSize: 12, opacity: 0.85 }} onClick={() => { setTab("admin"); setErr(""); setSuccess(""); }}>
                ⚖️ Equipe Vieira &amp; Melul: Criar acesso de Administrador →
              </button>
            </div>
          </div>
        )}

        {tab === "register" && (
          <div>
            <label className="field-label">Nome do Colégio / Instituição *</label>
            <input
              className="field-input"
              value={regName}
              onChange={e => setRegName(e.target.value)}
              placeholder="Ex: Colégio Santo Agostinho"
              autoFocus
            />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label className="field-label">Responsável / Cargo</label>
                <input
                  className="field-input"
                  value={regResp}
                  onChange={e => setRegResp(e.target.value)}
                  placeholder="Ex: Direção Geral"
                />
              </div>
              <div>
                <label className="field-label">WhatsApp para Avisos</label>
                <input
                  className="field-input"
                  value={regPhone}
                  onChange={e => setRegPhone(e.target.value)}
                  placeholder="(11) 99999-9999"
                />
              </div>
            </div>
            <label className="field-label">Usuário para Login *</label>
            <input
              className="field-input"
              value={regLogin}
              onChange={e => setRegLogin(e.target.value)}
              placeholder="Ex: santoagostinho"
            />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label className="field-label">Senha *</label>
                <input
                  className="field-input"
                  type="password"
                  value={regSenha}
                  onChange={e => setRegSenha(e.target.value)}
                  placeholder="Mínimo 4 dígitos"
                />
              </div>
              <div>
                <label className="field-label">Confirmar Senha *</label>
                <input
                  className="field-input"
                  type="password"
                  value={regSenhaConf}
                  onChange={e => setRegSenhaConf(e.target.value)}
                  placeholder="Repita a senha"
                  onKeyDown={e => e.key === "Enter" && handleRegister()}
                />
              </div>
            </div>
            <button className="login-btn" onClick={handleRegister} disabled={loading}>
              {loading ? "Cadastrando…" : "Concluir Cadastro e Entrar"}
            </button>
            <div style={{ textAlign: "center", marginTop: 18 }}>
              <button type="button" className="login-link" onClick={() => { setTab("login"); setErr(""); setSuccess(""); }}>
                ← Já possui conta? Clique para entrar
              </button>
            </div>
          </div>
        )}

        {tab === "admin" && (
          <div>
            <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: "#166534", lineHeight: 1.5 }}>
              🔒 <strong>Acesso Exclusivo Escritório:</strong> Cadastre novos advogados, consultores ou membros da equipe gestora da Vieira &amp; Melul.
            </div>
            <label className="field-label">Nome Completo do Administrador *</label>
            <input
              className="field-input"
              value={admName}
              onChange={e => setAdmName(e.target.value)}
              placeholder="Ex: Dra. Hellen Vieira"
              autoFocus
            />
            <label className="field-label">Cargo ou Função</label>
            <input
              className="field-input"
              value={admCargo}
              onChange={e => setAdmCargo(e.target.value)}
              placeholder="Ex: Sócia Diretora / Advogada Educacional"
            />
            <label className="field-label">Usuário para Login *</label>
            <input
              className="field-input"
              value={admLogin}
              onChange={e => setAdmLogin(e.target.value)}
              placeholder="Ex: hellen.adv"
            />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label className="field-label">Senha de Acesso *</label>
                <input
                  className="field-input"
                  type="password"
                  value={admSenha}
                  onChange={e => setAdmSenha(e.target.value)}
                  placeholder="Mínimo 4 dígitos"
                />
              </div>
              <div>
                <label className="field-label">Confirmar Senha *</label>
                <input
                  className="field-input"
                  type="password"
                  value={admSenhaConf}
                  onChange={e => setAdmSenhaConf(e.target.value)}
                  placeholder="Repita a senha"
                />
              </div>
            </div>
            <label className="field-label">Chave de Segurança do Escritório (PIN) *</label>
            <input
              className="field-input"
              type="password"
              value={admPin}
              onChange={e => setAdmPin(e.target.value)}
              placeholder="Digite a chave secreta do escritório"
              onKeyDown={e => e.key === "Enter" && handleRegisterAdmin()}
            />
            <button className="login-btn" onClick={handleRegisterAdmin} disabled={loading}>
              {loading ? "Criando administrador…" : "Criar Acesso de Administrador"}
            </button>
            <div style={{ textAlign: "center", marginTop: 18 }}>
              <button type="button" className="login-link" onClick={() => { setTab("login"); setErr(""); setSuccess(""); }}>
                ← Já possui acesso? Entrar no Portal
              </button>
            </div>
          </div>
        )}

        {err && <div className="login-err">{err}</div>}
        {success && <div className="login-success">{success}</div>}

        <div className="login-hint">
          Acesso restrito para escolas parceiras e equipe do escritório Vieira &amp; Melul.
        </div>
      </div>
    </div>
  );
}

// ─── HEADER MODERNO ─────────────────────────────────────────────────────────
function Header({ user, onLogout }) {
  const [, setPage] = useNavState();
  const initials = user.name
    ? user.name.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase()
    : "VM";

  return (
    <header className="header">
      <div className="header-brand" onClick={() => setPage("docs")}>
        <img src={LOGO_B64} alt="Vieira & Melul" style={{ height: 48, objectFit: "contain" }} />
      </div>
      <div className="header-r">
        <div className="user-badge" onClick={() => setPage("account")}>
          <div className="user-avatar">{initials}</div>
          <div className="user-info">
            <span className="user-name">{user.name}</span>
            <span className="user-role">{user.role === "admin" ? "Escritório (Administrador)" : "Colégio Parceiro"}</span>
          </div>
        </div>
        <button className="logout-btn" onClick={onLogout}>
          Sair
        </button>
      </div>
    </header>
  );
}

// ─── NAVEGAÇÃO LATERAL (SIDEBAR) ─────────────────────────────────────────────
const NavCtx = {
  listeners: [],
  value: "docs",
  params: null,
  set(v, params = null) {
    this.value = v;
    this.params = params;
    this.listeners.forEach(fn => fn(v, params));
  }
};

function useNavState() {
  const [v, setV] = useState(NavCtx.value);
  const [p, setP] = useState(NavCtx.params);

  useEffect(() => {
    const handler = (newV, newP) => { setV(newV); setP(newP); };
    NavCtx.listeners.push(handler);
    return () => { NavCtx.listeners = NavCtx.listeners.filter(f => f !== handler); };
  }, []);

  return [v, (nv, np = null) => NavCtx.set(nv, np), p];
}

function Sidebar({ user, unreadComms, pendingTasks }) {
  const [active, setActive] = useNavState();
  const isAdmin = user.role === "admin";

  // Menu Administrativo (Escritório posta e gerencia tudo)
  const navAdmin = [
    { id: "docs",      icon: "📁", label: "Documentos Publicados" },
    { id: "addDoc",    icon: "➕", label: "Publicar Documento" },
    { id: "tasks",     icon: "✅", label: "Pendências com Colégios" },
    { id: "addTask",   icon: "📋", label: "Criar Pendência" },
    { id: "comms",     icon: "📢", label: "Comunicados Oficiais" },
    { id: "addComm",   icon: "✉️", label: "Novo Comunicado" },
    { divider: true,   label: "Gestão" },
    { id: "schools",   icon: "🏫", label: "Colégios Cadastrados" },
    { id: "addSchool", icon: "➕", label: "Cadastrar Colégio" },
    { id: "account",   icon: "⚙️", label: "Configurações" },
  ];

  // Menu do Colégio (Apenas consulta, leitura, download e ciência)
  const navClient = [
    { id: "docs",    icon: "📁", label: "Documentos do Colégio" },
    { id: "tasks",   icon: "✅", label: "Pendências & Retornos" },
    { id: "comms",   icon: "📢", label: "Comunicados da Consultoria" },
    { divider: true, label: "Preferências" },
    { id: "account", icon: "⚙️", label: "Minha Conta / Perfil" },
  ];

  const items = isAdmin ? navAdmin : navClient;

  return (
    <aside className="sidebar">
      {items.map((item, idx) => {
        if (item.divider) {
          return <div key={idx} className="nav-section-title">{item.label}</div>;
        }
        return (
          <div
            key={item.id}
            className={`nav-item ${active === item.id ? "active" : ""}`}
            onClick={() => setActive(item.id)}
          >
            <span>{item.icon}</span>
            <span>{item.label}</span>
            {item.id === "comms" && unreadComms > 0 && <span className="nav-badge">{unreadComms}</span>}
            {item.id === "tasks" && pendingTasks > 0 && <span className="nav-badge nav-badge-gold">{pendingTasks}</span>}
          </div>
        );
      })}
    </aside>
  );
}

// ─── ROTEADOR PRINCIPAL ──────────────────────────────────────────────────────
function MainRouter(props) {
  const { user, schools, admins, docs, allDocs, tasks, allTasks, comms, readComms, markRead,
    addDoc, updateDocData, delDoc, addTask, delTask, updateTask, addComm, delComm,
    addSchool, updateSchool, delSchool, delAdmin } = props;

  const [page, setPage, navParams] = useNavState();

  if (page === "docs") {
    return (
      <DocsPage
        docs={docs}
        allDocs={allDocs}
        schools={schools}
        user={user}
        onDelete={delDoc}
        onUpdateDoc={updateDocData}
        onConfirmCiencia={async (docId) => {
          const target = allDocs.find(d => d.id === docId);
          if (!target) return;
          const currentList = target.ciencias || [];
          const novaCiencia = {
            schoolId: user.id,
            schoolName: user.name,
            dataHora: nowBRFull(),
          };
          await updateDocData(docId, { ciencias: [...currentList, novaCiencia] });
        }}
        onAddClick={(presetSchoolId) => setPage("addDoc", { schoolId: presetSchoolId })}
      />
    );
  }

  if (page === "addDoc") {
    // Apenas Administradores do Escritório podem acessar a tela de postagem
    if (user.role !== "admin") {
      setPage("docs");
      return null;
    }
    return (
      <AddDoc
        schools={schools}
        user={user}
        initialSchoolId={navParams?.schoolId}
        onCancel={() => setPage("docs")}
        onSave={async (d) => {
          await addDoc(d);
          setPage("docs");
        }}
      />
    );
  }

  if (page === "tasks") {
    return (
      <TasksPage
        tasks={tasks}
        schools={schools}
        user={user}
        onUpdate={updateTask}
        onDelete={delTask}
        onAddClick={() => setPage("addTask")}
      />
    );
  }

  if (page === "addTask") {
    if (user.role !== "admin") {
      setPage("tasks");
      return null;
    }
    return (
      <AddTask
        schools={schools}
        onCancel={() => setPage("tasks")}
        onSave={async (t) => {
          await addTask(t);
          setPage("tasks");
        }}
      />
    );
  }

  if (page === "comms") {
    return (
      <CommsPage
        comms={comms}
        schools={schools}
        user={user}
        readComms={readComms}
        markRead={markRead}
        onDelete={delComm}
        onAddClick={() => setPage("addComm")}
      />
    );
  }

  if (page === "addComm") {
    if (user.role !== "admin") {
      setPage("comms");
      return null;
    }
    return (
      <AddComm
        schools={schools}
        onCancel={() => setPage("comms")}
        onSave={async (c) => {
          await addComm(c);
          setPage("comms");
        }}
      />
    );
  }

  if (page === "schools") {
    if (user.role !== "admin") {
      setPage("docs");
      return null;
    }
    return (
      <SchoolList
        schools={schools}
        docs={allDocs}
        tasks={allTasks}
        onDelete={delSchool}
        onAddClick={() => setPage("addSchool")}
      />
    );
  }

  if (page === "addSchool") {
    if (user.role !== "admin") {
      setPage("docs");
      return null;
    }
    return (
      <AddSchool
        onCancel={() => setPage("schools")}
        onSave={async (s) => {
          await addSchool(s);
          setPage("schools");
        }}
      />
    );
  }

  if (page === "account") {
    return (
      <AccountPage
        user={user}
        admins={admins}
        onDeleteAdmin={delAdmin}
        onUpdateProfile={async (patch) => {
          if (user.role === "client") {
            await updateSchool(user.id, patch);
          }
        }}
      />
    );
  }

  return null;
}

// ─── PÁGINA DE DOCUMENTOS (MODELO ESCRITÓRIO VS COLÉGIO) ─────────────────────
function DocsPage({ docs, allDocs, schools, user, onDelete, onUpdateDoc, onConfirmCiencia, onAddClick }) {
  const isAdmin = user.role === "admin";
  const [adminTab, setAdminTab] = useState("all"); // "all" (Todos os docs) | "bySchool" (Por colégio)
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("Todos");
  const [statusFilter, setStatusFilter] = useState("Todos");
  const [schoolFilter, setSchoolFilter] = useState("all");
  const [previewDoc, setPreviewDoc] = useState(null);
  const [editDoc, setEditDoc] = useState(null);
  const [protocolDoc, setProtocolDoc] = useState(null);

  // ═════════════════════════════════════════════════════════════════════════════
  // 1. VISÃO DO ADMINISTRADOR (ESCRITÓRIO HELLEN VIEIRA)
  // ═════════════════════════════════════════════════════════════════════════════
  if (isAdmin) {
    let currentList = allDocs;
    if (adminTab === "bySchool" && selectedSchool) {
      currentList = allDocs.filter(d => d.schoolId === selectedSchool.id || d.schoolId === "all");
    } else if (adminTab === "all" && schoolFilter !== "all") {
      currentList = allDocs.filter(d => d.schoolId === schoolFilter || d.schoolId === "all");
    }

    const availableCats = ["Todos", ...new Set(currentList.map(d => d.categoria))];
    const filtered = currentList.filter(d => {
      const matchQuery = !q ||
        d.titulo.toLowerCase().includes(q.toLowerCase()) ||
        (d.descricao || "").toLowerCase().includes(q.toLowerCase());
      const matchCat = cat === "Todos" || d.categoria === cat;
      const matchStatus = statusFilter === "Todos" || (d.status || "Vigente") === statusFilter;
      return matchQuery && matchCat && matchStatus;
    });

    const totalCiencias = allDocs.reduce((acc, d) => acc + (d.ciencias || []).length, 0);

    return (
      <main className="main-content">
        <div className="welcome-banner">
          <div>
            <div className="welcome-banner-title">Painel de Gestão Jurídica — Vieira &amp; Melul</div>
            <div className="welcome-banner-sub">Controle central de pareceres, compliance educacional e ciências formais</div>
          </div>
          <div className="quick-stats-row">
            <div className="quick-stat-pill">
              <span>🏫</span>
              <div>
                <div className="quick-stat-pill-num">{schools.length}</div>
                <div style={{ fontSize: 10, opacity: 0.85 }}>Colégios</div>
              </div>
            </div>
            <div className="quick-stat-pill">
              <span>📁</span>
              <div>
                <div className="quick-stat-pill-num">{allDocs.length}</div>
                <div style={{ fontSize: 10, opacity: 0.85 }}>Documentos</div>
              </div>
            </div>
            <div className="quick-stat-pill">
              <span>✓</span>
              <div>
                <div className="quick-stat-pill-num">{totalCiencias}</div>
                <div style={{ fontSize: 10, opacity: 0.85 }}>Ciências</div>
              </div>
            </div>
          </div>
        </div>

        <div className="page-header-row">
          <div>
            <h1 className="page-title">Gestão de Documentos (Escritório)</h1>
            <p className="page-sub">Publicação e controle de documentos para todos os colégios parceiros</p>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="secondary-btn" onClick={() => exportReportCSV(schools, allDocs, [])}>
              📥 Exportar Relatório (CSV)
            </button>
            <button className="primary-btn" onClick={() => onAddClick(selectedSchool ? selectedSchool.id : null)}>
              <span>➕</span> Publicar Novo Documento
            </button>
          </div>
        </div>

        {/* Abas do Administrador: Todos os Docs vs Por Colégio */}
        <div className="login-tabs" style={{ maxWidth: 440, marginBottom: 20 }}>
          <div
            className={`login-tab ${adminTab === "all" ? "active" : ""}`}
            onClick={() => { setAdminTab("all"); setSelectedSchool(null); }}
          >
            📋 Todos os Documentos ({allDocs.length})
          </div>
          <div
            className={`login-tab ${adminTab === "bySchool" ? "active" : ""}`}
            onClick={() => setAdminTab("bySchool")}
          >
            🏫 Por Colégio ({schools.length})
          </div>
        </div>

        {/* MODO POR COLÉGIO: SELEÇÃO DE PASTA */}
        {adminTab === "bySchool" && !selectedSchool ? (
          <div>
            <p style={{ fontSize: 14, color: T.muted, marginBottom: 16 }}>
              Clique em um colégio para abrir a pasta com os documentos específicos:
            </p>
            <div className="cards-grid">
              {schools.map(s => {
                const count = allDocs.filter(d => d.schoolId === s.id || d.schoolId === "all").length;
                return (
                  <div
                    key={s.id}
                    className="doc-card"
                    style={{ cursor: "pointer" }}
                    onClick={() => { setSelectedSchool(s); setQ(""); setCat("Todos"); }}
                  >
                    <div>
                      <div className="doc-category-badge">🏫 Pasta do Colégio</div>
                      <h3 className="doc-heading" style={{ fontSize: 18 }}>{s.name}</h3>
                      <div className="doc-description">
                        {s.responsavel ? `Responsável: ${s.responsavel}` : `Login: ${s.login}`}
                      </div>
                    </div>
                    <div>
                      <div className="doc-footer-meta">
                        <span className="doc-date-text">{count} documento{count !== 1 ? "s" : ""}</span>
                        <span className="badge-pill badge-pill-green">Abrir pasta →</span>
                      </div>
                      <div className="doc-actions-row">
                        <button
                          className="action-btn"
                          onClick={(e) => { e.stopPropagation(); onAddClick(s.id); }}
                        >
                          ➕ Postar Documento
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div>
            {/* Se estiver filtrado por colégio, mostra botão de voltar */}
            {selectedSchool && (
              <button
                onClick={() => setSelectedSchool(null)}
                style={{ background: "none", border: "none", color: T.accent, cursor: "pointer", fontSize: 13, fontWeight: 700, marginBottom: 14, display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                ← Voltar para lista de colégios
              </button>
            )}

            {/* Busca e Filtros Avançados */}
            <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
              <div className="search-box" style={{ flex: 1, minWidth: 260, margin: 0 }}>
                <span className="search-icon-svg">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                </span>
                <input
                  className="search-input"
                  placeholder={selectedSchool ? `Buscar em ${selectedSchool.name}…` : "Buscar em todos os documentos publicados…"}
                  value={q}
                  onChange={e => setQ(e.target.value)}
                />
              </div>

              {adminTab === "all" && (
                <select
                  className="form-control-input"
                  style={{ width: "auto", minWidth: 220, padding: "10px 14px", margin: 0 }}
                  value={schoolFilter}
                  onChange={e => setSchoolFilter(e.target.value)}
                >
                  <option value="all">🏫 Filtrar por Colégio (Todos)</option>
                  {schools.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
              <div className="filter-chips" style={{ margin: 0 }}>
                {availableCats.map(c => (
                  <button
                    key={c}
                    className={`chip ${cat === c ? "active" : ""}`}
                    onClick={() => setCat(c)}
                  >
                    {c}
                  </button>
                ))}
              </div>

              <div className="filter-chips" style={{ margin: 0 }}>
                {["Todos", "Vigente", "Em Análise", "Revogado"].map(st => (
                  <button
                    key={st}
                    className={`chip ${statusFilter === st ? "active" : ""}`}
                    onClick={() => setStatusFilter(st)}
                  >
                    {st === "Vigente" ? "🟢 Vigentes" : st === "Em Análise" ? "🟡 Em Análise" : st === "Revogado" ? "🔴 Revogados" : "Status: Todos"}
                  </button>
                ))}
              </div>
            </div>

            {/* Lista de Documentos */}
            {filtered.length > 0 ? (
              <div className="cards-grid">
                {filtered.map(d => {
                  const targetSchool = schools.find(s => s.id === d.schoolId);
                  const destLabel = d.schoolId === "all" ? "📣 Todos os Colégios" : (targetSchool?.name || "Colégio Específico");
                  const cienciasCount = (d.ciencias || []).length;

                  return (
                    <div key={d.id} className="doc-card">
                      <div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                          <div className="doc-category-badge">
                            {d.categoria}
                          </div>
                          <button
                            className="whatsapp-btn"
                            title="Compartilhar no WhatsApp com o colégio"
                            onClick={() => shareOnWhatsApp(d.titulo, d.categoria, d.link, d.descricao, targetSchool?.telefone)}
                          >
                            💬 WhatsApp
                          </button>
                        </div>

                        <div style={{ fontSize: 12, fontWeight: 700, color: d.schoolId === "all" ? T.purple : T.blue, marginBottom: 6 }}>
                          🏫 Destinatário: {destLabel}
                        </div>

                        <h3 className="doc-heading" style={{ cursor: "pointer" }} onClick={() => setPreviewDoc(d)}>
                          {d.titulo}
                        </h3>

                        {cienciasCount > 0 ? (
                          <div className="ciencia-tag" style={{ background: T.blueDim, color: T.blue }}>
                            ✓ {cienciasCount} colégio(s) acusaram ciência
                          </div>
                        ) : (
                          <div style={{ fontSize: 11, color: T.muted, marginBottom: 8 }}>
                            Aguardando confirmação de ciência
                          </div>
                        )}

                        {d.descricao && <p className="doc-description">{d.descricao}</p>}
                      </div>

                      <div>
                        <div className="doc-footer-meta">
                          <span className="doc-date-text">{d.data}</span>
                          <span className={`badge-pill ${d.status === "Vigente" ? "badge-pill-green" : d.status === "Em Análise" ? "badge-pill-gold" : "badge-pill-purple"}`}>
                            {d.status || "Vigente"}
                          </span>
                        </div>

                        <div className="doc-actions-row">
                          <button className="action-btn" onClick={() => setPreviewDoc(d)}>
                            👁️ Detalhes
                          </button>
                          <button className="action-btn" onClick={() => setEditDoc(d)}>
                            ✏️ Editar
                          </button>
                          {d.link && (
                            <a href={d.link} target="_blank" rel="noreferrer" className="action-btn" style={{ flex: 1 }}>
                              <span>↗</span> Acessar
                            </a>
                          )}
                          <button
                            className="action-btn action-btn-danger"
                            onClick={() => {
                              if (window.confirm(`Remover "${d.titulo}"?`)) onDelete(d.id);
                            }}
                          >
                            Excluir
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-box">
                <div className="empty-icon-lg">📂</div>
                <div className="empty-title">Nenhum documento encontrado</div>
                <p className="empty-desc">Você ainda não publicou documentos com esse filtro.</p>
                <button className="primary-btn" onClick={() => onAddClick(selectedSchool ? selectedSchool.id : null)}>
                  <span>➕</span> Publicar Documento Agora
                </button>
              </div>
            )}
          </div>
        )}

        {/* Modal de Pré-visualização do Admin */}
        {previewDoc && (
          <DocPreviewModal
            docItem={previewDoc}
            isAdmin={true}
            user={user}
            onClose={() => setPreviewDoc(null)}
          />
        )}
      </main>
    );
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // 2. VISÃO DO COLÉGIO (ESCOLAS PARCEIRAS - APENAS CONSULTA E DOWNLOAD)
  // ═════════════════════════════════════════════════════════════════════════════
  const availableCats = ["Todos", ...new Set(docs.map(d => d.categoria))];
  const filtered = docs.filter(d => {
    const matchQuery = !q ||
      d.titulo.toLowerCase().includes(q.toLowerCase()) ||
      (d.descricao || "").toLowerCase().includes(q.toLowerCase());
    const matchCat = cat === "Todos" || d.categoria === cat;
    return matchQuery && matchCat;
  });

  return (
    <main className="main-content">
      <div className="welcome-banner">
        <div>
          <div className="welcome-banner-title">Bem-vindo(a), {user.name}!</div>
          <div className="welcome-banner-sub">Acervo documental e pareceres jurídicos da consultoria Vieira &amp; Melul</div>
        </div>
        <div className="quick-stats-row">
          <div className="quick-stat-pill">
            <span>📁</span>
            <div>
              <div className="quick-stat-pill-num">{docs.length}</div>
              <div style={{ fontSize: 10, opacity: 0.85 }}>Documentos</div>
            </div>
          </div>
          <div className="quick-stat-pill">
            <span>✓</span>
            <div>
              <div className="quick-stat-pill-num">
                {docs.filter(d => (d.ciencias || []).some(c => c.schoolId === user.id)).length}
              </div>
              <div style={{ fontSize: 10, opacity: 0.85 }}>Ciências Registradas</div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-header-row">
        <div>
          <h1 className="page-title">Documentos e Pareceres Oficiais</h1>
          <p className="page-sub">Acervo disponibilizado pela consultoria Vieira &amp; Melul para sua instituição</p>
        </div>
      </div>

      {/* Busca e Filtros do Colégio */}
      <div className="search-box">
        <span className="search-icon-svg">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
        </span>
        <input
          className="search-input"
          placeholder="Pesquisar documentos, pareceres, contratos…"
          value={q}
          onChange={e => setQ(e.target.value)}
        />
      </div>

      <div className="filter-chips">
        {availableCats.map(c => (
          <button
            key={c}
            className={`chip ${cat === c ? "active" : ""}`}
            onClick={() => setCat(c)}
          >
            {c}
          </button>
        ))}
      </div>

      {filtered.length > 0 ? (
        <div className="cards-grid">
          {filtered.map(d => {
            const catObj = CATEGORIAS_DOCS.find(c => c.id === d.categoria);
            const icon = catObj ? catObj.icon : "📄";
            const ciencias = d.ciencias || [];
            const hasCiencia = ciencias.some(c => c.schoolId === user.id);

            return (
              <div key={d.id} className="doc-card">
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                    <div className="doc-category-badge">
                      <span>{icon}</span> {d.categoria}
                    </div>
                  </div>

                  <h3 className="doc-heading" style={{ cursor: "pointer" }} onClick={() => setPreviewDoc(d)}>
                    {d.titulo}
                  </h3>

                  {/* Tag de Ciência */}
                  {hasCiencia ? (
                    <div className="ciencia-tag">
                      ✓ Ciência acusada em {ciencias.find(c => c.schoolId === user.id)?.dataHora}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: T.gold, fontWeight: 600, marginBottom: 8 }}>
                      ⚠️ Pendente de acusação de ciência
                    </div>
                  )}

                  {d.descricao && <p className="doc-description">{d.descricao}</p>}
                </div>

                <div>
                  <div className="doc-footer-meta">
                    <span className="doc-date-text">{d.data}</span>
                    <span className={`badge-pill ${d.status === "Vigente" ? "badge-pill-green" : "badge-pill-gold"}`}>
                      {d.status || "Vigente"}
                    </span>
                  </div>

                  <div className="doc-actions-row">
                    <button className="action-btn" onClick={() => setPreviewDoc(d)}>
                      👁️ Visualizar
                    </button>

                    {d.link && (
                      <a href={d.link} target="_blank" rel="noreferrer" className="action-btn" style={{ flex: 1 }}>
                        <span>📥</span> Baixar / Acessar
                      </a>
                    )}

                    {hasCiencia && (
                      <button
                        className="action-btn"
                        style={{ borderColor: T.accent, color: T.accent }}
                        onClick={() => {
                          const myCiencia = ciencias.find(c => c.schoolId === user.id);
                          setProtocolDoc({ doc: d, hora: myCiencia?.dataHora || nowBRFull() });
                        }}
                      >
                        🖨️ Protocolo
                      </button>
                    )}

                    {!hasCiencia && (
                      <button
                        className="action-btn"
                        style={{ borderColor: T.accent, color: T.accent, background: T.accentDim }}
                        onClick={() => onConfirmCiencia(d.id)}
                      >
                        ✍️ Acusar Ciência
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="empty-box">
          <div className="empty-icon-lg">📁</div>
          <div className="empty-title">Nenhum documento disponível no momento</div>
          <p className="empty-desc">
            Os pareceres, regimentos e documentos oficiais elaborados pela consultoria Vieira &amp; Melul para a sua escola aparecerão automaticamente aqui.
          </p>
        </div>
      )}

      {/* Modais da Página */}
      {previewDoc && (
        <DocPreviewModal
          docItem={previewDoc}
          isAdmin={isAdmin}
          user={user}
          schools={schools}
          onConfirmCiencia={onConfirmCiencia}
          onOpenProtocol={(p) => setProtocolDoc(p)}
          onClose={() => setPreviewDoc(null)}
        />
      )}

      {editDoc && (
        <EditDocModal
          docItem={editDoc}
          schools={schools}
          onSave={onUpdateDoc}
          onClose={() => setEditDoc(null)}
        />
      )}

      {protocolDoc && (
        <ProtocolModal
          docItem={protocolDoc.doc}
          user={user}
          dataHora={protocolDoc.hora}
          onClose={() => setProtocolDoc(null)}
        />
      )}
    </main>
  );
}

// ─── COMPONENTE MODAL DE PRÉ-VISUALIZAÇÃO ────────────────────────────────────
function DocPreviewModal({ docItem, isAdmin, user, schools = [], onConfirmCiencia, onOpenProtocol, onClose }) {
  const ciencias = docItem.ciencias || [];
  const hasCiencia = !isAdmin && ciencias.some(c => c.schoolId === user.id);
  const myCiencia = ciencias.find(c => c.schoolId === user.id);

  // No modo Administrador, se for para "all", identifica quais colégios estão pendentes
  const pendingSchools = isAdmin && docItem.schoolId === "all"
    ? schools.filter(s => !ciencias.some(c => c.schoolId === s.id))
    : [];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" style={{ maxWidth: 680 }} onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>

        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <span className="doc-category-badge" style={{ margin: 0 }}>
            {docItem.categoria}
          </span>
          <span className={`badge-pill ${docItem.status === "Vigente" ? "badge-pill-green" : docItem.status === "Em Análise" ? "badge-pill-gold" : "badge-pill-purple"}`}>
            {docItem.status || "Vigente"}
          </span>
        </div>

        <h2 style={{ fontFamily: "Syne", fontSize: 22, fontWeight: 800, color: "#0F172A", marginBottom: 8 }}>
          {docItem.titulo}
        </h2>

        <div style={{ fontSize: 13, color: T.muted, marginBottom: 20 }}>
          Publicado em <strong>{docItem.data}</strong> pela Consultoria Jurídica Educacional Vieira &amp; Melul
        </div>

        {docItem.descricao && (
          <div style={{ background: "#F8FAFC", padding: 18, borderRadius: 12, border: "1px solid #E2E8F0", marginBottom: 20, fontSize: 14, lineHeight: 1.7, color: "#334155" }}>
            <strong>Orientações e Diretrizes:</strong>
            <p style={{ marginTop: 6, whiteSpace: "pre-wrap" }}>{docItem.descricao}</p>
          </div>
        )}

        {/* Quadro de Ciência Formal & Rastreabilidade */}
        <div style={{ background: "#F1F5F9", padding: 20, borderRadius: 12, marginBottom: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h4 style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", color: "#334155" }}>
              📋 Rastreabilidade de Leitura &amp; Ciência
            </h4>
            {!isAdmin && !hasCiencia && (
              <button
                className="primary-btn"
                style={{ padding: "6px 14px", fontSize: 12 }}
                onClick={() => {
                  onConfirmCiencia(docItem.id);
                  onClose();
                }}
              >
                ✍️ Acusar Ciência Agora
              </button>
            )}
            {!isAdmin && hasCiencia && (
              <button
                className="secondary-btn"
                style={{ padding: "6px 14px", fontSize: 12, borderColor: T.accent, color: T.accent }}
                onClick={() => onOpenProtocol({ doc: docItem, hora: myCiencia?.dataHora || nowBRFull() })}
              >
                🖨️ Protocolo de Ciência
              </button>
            )}
          </div>

          {ciencias.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {ciencias.map((c, i) => (
                <div key={i} style={{ fontSize: 12, color: T.accentText, display: "flex", alignItems: "center", justifyContent: "space-between", background: "#FFFFFF", padding: "8px 12px", borderRadius: 8, border: "1px solid #E2E8F0" }}>
                  <div>
                    <span>✓</span> <strong>{c.schoolName}</strong> acusou ciência
                  </div>
                  <span style={{ fontSize: 11, color: T.muted, fontFamily: "JetBrains Mono" }}>{c.dataHora}</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: T.muted }}>
              Nenhum registro de ciência confirmado para este documento ainda.
            </div>
          )}

          {/* Se o Admin estiver visualizando e houver colégios pendentes */}
          {isAdmin && pendingSchools.length > 0 && (
            <div style={{ marginTop: 16, borderTop: "1px dashed #CBD5E1", paddingTop: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: T.gold, marginBottom: 8 }}>
                ⚠️ Colégios com Ciência Pendente ({pendingSchools.length}):
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 180, overflowY: "auto" }}>
                {pendingSchools.map(ps => (
                  <div key={ps.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, background: "#FFFBEB", padding: "6px 12px", borderRadius: 8, border: "1px solid #FEF3C7" }}>
                    <span style={{ fontWeight: 600, color: "#92400E" }}>{ps.name}</span>
                    <button
                      className="whatsapp-btn"
                      style={{ padding: "4px 10px", fontSize: 11 }}
                      onClick={() => shareOnWhatsApp(
                        docItem.titulo,
                        docItem.categoria,
                        docItem.link,
                        "Notificamos que o documento aguarda confirmação formal de ciência no portal da consultoria.",
                        ps.telefone
                      )}
                    >
                      💬 Cobrar Ciência
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Ações */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", borderTop: "1px solid #E2E8F0", paddingTop: 20 }}>
          {docItem.link && (
            <a href={docItem.link} target="_blank" rel="noreferrer" className="primary-btn" style={{ flex: 1, justifyContent: "center" }}>
              <span>📥</span> Baixar / Acessar Documento
            </a>
          )}
          <button
            className="whatsapp-btn"
            style={{ flex: 1, justifyContent: "center", fontSize: 13 }}
            onClick={() => shareOnWhatsApp(docItem.titulo, docItem.categoria, docItem.link, docItem.descricao)}
          >
            <span>💬 Compartilhar no WhatsApp</span>
          </button>
          <button className="secondary-btn" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MODAL DE EDIÇÃO DE DOCUMENTO (EXCLUSIVO ESCRITÓRIO) ─────────────────────
function EditDocModal({ docItem, schools, onSave, onClose }) {
  const [f, setF] = useState({
    titulo: docItem.titulo || "",
    categoria: docItem.categoria || CATEGORIAS_DOCS[0].id,
    status: docItem.status || "Vigente",
    schoolId: docItem.schoolId || "all",
    link: docItem.link || "",
    descricao: docItem.descricao || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!f.titulo.trim()) { alert("Informe o título do documento."); return; }
    setSaving(true);
    await onSave(docItem.id, f);
    setSaving(false);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <h2 style={{ fontFamily: "Syne", fontSize: 20, fontWeight: 800, color: "#0F172A", marginBottom: 16 }}>
          ✏️ Editar Documento
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group-item">
            <label className="field-label">Título do Documento *</label>
            <input
              className="form-control-input"
              value={f.titulo}
              onChange={e => setF({ ...f, titulo: e.target.value })}
              required
            />
          </div>

          <div className="form-grid-2">
            <div className="form-group-item">
              <label className="field-label">Categoria</label>
              <select className="form-control-input" value={f.categoria} onChange={e => setF({ ...f, categoria: e.target.value })}>
                {CATEGORIAS_DOCS.map(c => <option key={c.id} value={c.id}>{c.icon} {c.id}</option>)}
              </select>
            </div>
            <div className="form-group-item">
              <label className="field-label">Status do Documento</label>
              <select className="form-control-input" value={f.status} onChange={e => setF({ ...f, status: e.target.value })}>
                <option value="Vigente">🟢 Vigente / Oficial</option>
                <option value="Em Análise">🟡 Em Análise / Minuta</option>
                <option value="Revogado">🔴 Revogado / Arquivado</option>
              </select>
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group-item">
              <label className="field-label">Colégio Destinatário</label>
              <select className="form-control-input" value={f.schoolId} onChange={e => setF({ ...f, schoolId: e.target.value })}>
                <option value="all">📣 Todos os Colégios</option>
                {schools.map(s => <option key={s.id} value={s.id}>🏫 {s.name}</option>)}
              </select>
            </div>
            <div className="form-group-item">
              <label className="field-label">Link do Arquivo (URL)</label>
              <input className="form-control-input" value={f.link} onChange={e => setF({ ...f, link: e.target.value })} placeholder="https://..." />
            </div>
          </div>

          <div className="form-group-item">
            <label className="field-label">Orientações e Diretrizes</label>
            <textarea className="form-control-input" value={f.descricao} onChange={e => setF({ ...f, descricao: e.target.value })} />
          </div>

          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <button type="button" className="secondary-btn" onClick={onClose}>Cancelar</button>
            <button type="submit" className="primary-btn" disabled={saving}>
              {saving ? "Salvando…" : "Salvar Alterações"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── MODAL DE PROTOCOLO DE CIÊNCIA (COMPROVANTE IMPRESSO) ────────────────────
function ProtocolModal({ docItem, user, dataHora, onClose }) {
  const protocolNumber = `VM-${Date.now().toString(36).toUpperCase()}-${docItem.id.slice(0, 4).toUpperCase()}`;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" style={{ maxWidth: 700 }} onClick={e => e.stopPropagation()}>
        <button className="modal-close no-print" onClick={onClose}>✕</button>

        <div className="printable-protocol">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "2px solid #166534", paddingBottom: 16, marginBottom: 20 }}>
            <div>
              <h2 style={{ fontFamily: "Syne", fontSize: 20, fontWeight: 800, color: "#166534" }}>VIEIRA &amp; MELUL</h2>
              <p style={{ fontSize: 11, color: "#64748B", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                Consultoria Jurídica Educacional
              </p>
            </div>
            <div style={{ textAlign: "right", fontSize: 11, color: "#64748B" }}>
              <div><strong>Protocolo:</strong> {protocolNumber}</div>
              <div>Data: {nowBR()}</div>
            </div>
          </div>

          <div style={{ textAlign: "center", margin: "24px 0" }}>
            <span style={{ background: "#DCFCE7", color: "#166534", padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
              ✓ COMPROVANTE FORMAL DE CIÊNCIA INSTITUCIONAL
            </span>
          </div>

          <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: 18, marginBottom: 20, fontSize: 13, lineHeight: 1.8 }}>
            <div><strong>Documento Notificado:</strong> {docItem.titulo}</div>
            <div><strong>Categoria:</strong> {docItem.categoria}</div>
            <div><strong>Instituição Notificada:</strong> {user.name}</div>
            <div><strong>Responsável / Usuário:</strong> {user.responsavel || user.login}</div>
            <div><strong>Data e Hora do Registro:</strong> {dataHora}</div>
            <div><strong>Status da Notificação:</strong> Concluída com Validade Jurídica</div>
          </div>

          <p style={{ fontSize: 12, color: "#475569", lineHeight: 1.6, textAlign: "justify", marginBottom: 28 }}>
            Certificamos para os devidos fins legais, regulatórios e de conformidade que a instituição de ensino acima qualificada acusou o recebimento, leitura e inequívoca ciência das diretrizes, pareceres ou minutas contidas no presente documento, emitido pelo escritório de advocacia e consultoria educacional Vieira &amp; Melul.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 30, borderTop: "1px dashed #CBD5E1", paddingTop: 24, textAlign: "center", fontSize: 12 }}>
            <div>
              <div style={{ fontWeight: 700, color: "#166534" }}>Vieira &amp; Melul Consultoria</div>
              <div style={{ fontSize: 11, color: "#64748B" }}>Departamento Jurídico Educacional</div>
            </div>
            <div>
              <div style={{ fontWeight: 700, color: "#1E293B" }}>{user.name}</div>
              <div style={{ fontSize: 11, color: "#64748B" }}>Direção / Gestão Institucional</div>
            </div>
          </div>
        </div>

        <div className="no-print" style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 20 }}>
          <button className="secondary-btn" onClick={onClose}>Fechar</button>
          <button className="primary-btn" onClick={() => window.print()}>
            <span>🖨️</span> Imprimir Comprovante (PDF)
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── FORMULÁRIO EXCLUSIVO DO ADMINISTRADOR: PUBLICAR DOCUMENTO ──────────────
function AddDoc({ schools, user, initialSchoolId, onSave, onCancel }) {
  const [f, setF] = useState({
    titulo: "",
    descricao: "",
    categoria: CATS[0],
    schoolId: initialSchoolId || "all", // "all" para todos os colégios ou específico
    link: "",
    status: "Vigente",
  });

  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState(false);

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selected = e.target.files[0];
      setFile(selected);
      if (!f.titulo) {
        const nameWithoutExt = selected.name.replace(/\.[^/.]+$/, "");
        set("titulo", nameWithoutExt);
      }
    }
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!f.titulo.trim()) {
      alert("Por favor, informe o título do documento.");
      return;
    }

    setSaving(true);

    try {
      let finalLink = f.link.trim();

      // Upload no Firebase Storage caso anexado arquivo
      if (file) {
        const fileExt = file.name.split(".").pop();
        const storagePath = `documentos/${f.schoolId}/${Date.now()}_${uid()}.${fileExt}`;
        const fileRef = ref(storage, storagePath);
        const uploadTask = uploadBytesResumable(fileRef, file);

        await new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            (snapshot) => {
              const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
              setUploadProgress(progress);
            },
            (error) => {
              console.error("Erro no upload do arquivo:", error);
              reject(error);
            },
            async () => {
              finalLink = await getDownloadURL(uploadTask.snapshot.ref);
              resolve();
            }
          );
        });
      }

      await onSave({
        ...f,
        link: finalLink,
        id: uid(),
        data: nowBR(),
        enviadoPor: user.name,
        ciencias: [],
      });
      setOk(true);
    } catch (err) {
      console.error(err);
      alert("Erro ao publicar documento. Verifique os dados ou a conexão.");
      setSaving(false);
    }
  };

  return (
    <main className="main-content">
      <div className="page-header-row">
        <div>
          <button
            onClick={onCancel}
            style={{ background: "none", border: "none", color: T.accent, cursor: "pointer", fontSize: 13, fontWeight: 700, marginBottom: 8, display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            ← Voltar para documentos
          </button>
          <h1 className="page-title">Publicar Documento (Escritório)</h1>
          <p className="page-sub">Disponibilize pareceres, minutas, contratos ou circulares para os colégios</p>
        </div>
      </div>

      <div className="form-panel">
        <form onSubmit={submit}>
          <div className="form-group-item">
            <label className="field-label">Título do Documento *</label>
            <input
              className="form-control-input"
              value={f.titulo}
              onChange={e => set("titulo", e.target.value)}
              placeholder="Ex: Parecer Jurídico sobre BNCC, Regimento Interno 2026..."
              autoFocus
            />
          </div>

          <div className="form-grid-2">
            <div className="form-group-item">
              <label className="field-label">Colégio Destinatário *</label>
              <select
                className="form-control-input"
                value={f.schoolId}
                onChange={e => set("schoolId", e.target.value)}
              >
                <option value="all">📣 Todos os Colégios (Documento Geral)</option>
                {schools.map(s => <option key={s.id} value={s.id}>🏫 {s.name}</option>)}
              </select>
            </div>

            <div className="form-group-item">
              <label className="field-label">Categoria</label>
              <select
                className="form-control-input"
                value={f.categoria}
                onChange={e => set("categoria", e.target.value)}
              >
                {CATEGORIAS_DOCS.map(c => (
                  <option key={c.id} value={c.id}>{c.icon} {c.id}</option>
                ))}
              </select>
            </div>
          </div>

          {/* UPLOAD DE ARQUIVO */}
          <div className="form-group-item">
            <label className="field-label">Anexar Arquivo (PDF, Word, Imagens)</label>
            <div className="upload-dropzone" onClick={() => document.getElementById("file-upload-input").click()}>
              <input
                id="file-upload-input"
                type="file"
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              <div style={{ fontSize: 28, marginBottom: 8 }}>📎</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#1E293B" }}>
                {file ? `Arquivo: ${file.name}` : "Clique para selecionar o arquivo do seu computador"}
              </div>
              <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>
                {file ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : "PDF, DOCX, imagens até 20MB (salvo no Firebase Storage)"}
              </div>
            </div>

            {uploadProgress !== null && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, fontWeight: 600, color: T.accentText }}>
                  <span>Enviando arquivo para nuvem…</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="upload-progress-bar">
                  <div className="upload-progress-fill" style={{ width: `${uploadProgress}%` }} />
                </div>
              </div>
            )}
          </div>

          {/* OU LINK EXTERNO */}
          <div className="form-grid-2">
            <div className="form-group-item">
              <label className="field-label">Ou Link Externo (Google Drive, Dropbox, etc.)</label>
              <input
                className="form-control-input"
                value={f.link}
                onChange={e => set("link", e.target.value)}
                placeholder="https://drive.google.com/file/d/…"
              />
            </div>

            <div className="form-group-item">
              <label className="field-label">Status</label>
              <select
                className="form-control-input"
                value={f.status}
                onChange={e => set("status", e.target.value)}
              >
                <option value="Vigente">Vigente / Oficial</option>
                <option value="Em revisão">Em revisão / Análise</option>
                <option value="Pendente">Pendente de assinatura</option>
              </select>
            </div>
          </div>

          <div className="form-group-item">
            <label className="field-label">Orientações Legais e Resumo</label>
            <textarea
              className="form-control-input"
              value={f.descricao}
              onChange={e => set("descricao", e.target.value)}
              placeholder="Descreva as orientações para o colégio, fundamentação ou instruções importantes…"
            />
          </div>

          <div style={{ display: "flex", gap: 12, marginTop: 10 }}>
            <button type="submit" className="primary-btn" disabled={saving}>
              {saving ? "Publicando…" : "✓ Publicar Documento para o Colégio"}
            </button>
            <button type="button" className="secondary-btn" onClick={onCancel}>Cancelar</button>
          </div>

          {ok && <div className="login-success" style={{ textAlign: "left", marginTop: 16 }}>✓ Documento publicado com sucesso!</div>}
        </form>
      </div>
    </main>
  );
}

// ─── PÁGINA DE PENDÊNCIAS ───────────────────────────────────────────────────
function TasksPage({ tasks, schools, user, onUpdate, onDelete, onAddClick }) {
  const isAdmin = user.role === "admin";
  const [filter, setFilter] = useState("all");
  const [suggInput, setSuggInput] = useState({});
  const [suggFile, setSuggFile] = useState({});
  const [uploadingTask, setUploadingTask] = useState({});

  const now2 = new Date();
  const enriched = tasks.map(t => {
    let status = t.status;
    if (status === "pending" && t.deadline) {
      const [d, m, y] = t.deadline.split("/");
      if (new Date(+y, +m - 1, +d) < now2) status = "overdue";
    }
    return { ...t, status };
  });

  const filtered = filter === "all" ? enriched : enriched.filter(t => t.status === filter);
  const statusMap = { pending: "Aguardando", approved: "Aprovado", revision: "Sugestão enviada", overdue: "Atrasado" };
  const badgeMap  = { pending: "badge-pill-gold", approved: "badge-pill-green", revision: "badge-pill-blue", overdue: "badge-pill-red" };

  return (
    <main className="main-content">
      <div className="page-header-row">
        <div>
          <h1 className="page-title">{isAdmin ? "Pendências com Colégios" : "Pendências & Retornos"}</h1>
          <p className="page-sub">Documentos e minutas aguardando retorno, aprovação ou parecer</p>
        </div>
        {isAdmin && (
          <button className="primary-btn" onClick={onAddClick}>
            <span>➕</span> Criar Pendência
          </button>
        )}
      </div>

      <div className="filter-chips">
        {[
          ["all", "Todas"],
          ["pending", "Aguardando"],
          ["overdue", "Atrasadas"],
          ["approved", "Aprovadas"],
          ["revision", "Sugestão Enviada"],
        ].map(([k, label]) => (
          <button
            key={k}
            className={`chip ${filter === k ? "active" : ""}`}
            onClick={() => setFilter(k)}
          >
            {label}
          </button>
        ))}
      </div>

      {!filtered.length ? (
        <div className="empty-box">
          <div className="empty-icon-lg">✅</div>
          <div className="empty-title">Nenhuma pendência encontrada</div>
          <p className="empty-desc">Tudo em dia! Não há pendências registradas com esse filtro.</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {filtered.map(t => {
            const school = schools.find(s => s.id === t.schoolId);
            const ds = deadlineStatus(t.deadline);

            return (
              <div
                key={t.id}
                className="stat-card"
                style={{
                  borderLeft: `4px solid ${t.status === "approved" ? T.accent : t.status === "overdue" ? T.danger : t.status === "revision" ? T.blue : T.gold}`,
                  padding: 24
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 12 }}>
                  <div>
                    <h3 style={{ fontFamily: "Syne", fontSize: 17, fontWeight: 700, color: "#0F172A", marginBottom: 4 }}>
                      {t.titulo}
                    </h3>
                    {isAdmin && school && (
                      <div style={{ fontSize: 13, color: T.blue, fontWeight: 600 }}>
                        🏫 {school.name}
                      </div>
                    )}
                    {t.deadline && (
                      <div style={{ fontSize: 12, marginTop: 4, fontFamily: "JetBrains Mono", color: ds === "late" ? T.danger : ds === "warn" ? T.gold : T.muted }}>
                        {ds === "late" ? "⚠️ Prazo vencido em: " : "Prazo limite: "} {t.deadline}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <button
                      className="whatsapp-btn"
                      onClick={() => shareOnWhatsApp(`Pendência: ${t.titulo}`, "Retorno Jurídico", "", t.descricao)}
                    >
                      💬 WhatsApp
                    </button>
                    <span className={`badge-pill ${badgeMap[t.status] || "badge-pill-gold"}`}>
                      {statusMap[t.status] || t.status}
                    </span>
                  </div>
                </div>

                {t.descricao && (
                  <p style={{ fontSize: 14, color: T.mutedLight, lineHeight: 1.6, marginBottom: 16 }}>
                    {t.descricao}
                  </p>
                )}

                {t.suggestion && (
                  <div style={{ background: "#F1F5F9", borderRadius: 10, padding: 14, marginBottom: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: T.blue }}>
                        💬 Retorno do Colégio:
                      </div>
                      {t.returnData && (
                        <span style={{ fontSize: 11, color: T.muted, fontFamily: "JetBrains Mono" }}>{t.returnData}</span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: "#334155", lineHeight: 1.5 }}>{t.suggestion}</div>
                    {t.returnFile && (
                      <div style={{ marginTop: 10 }}>
                        <a
                          href={t.returnFile}
                          target="_blank"
                          rel="noreferrer"
                          className="action-btn"
                          style={{ display: "inline-flex", width: "auto", borderColor: T.accent, color: T.accent, background: T.accentDim, padding: "6px 12px", fontSize: 12 }}
                        >
                          <span>📎</span> Baixar Arquivo Anexado ({t.returnFileName || "Ver Arquivo"})
                        </a>
                      </div>
                    )}
                  </div>
                )}

                {!isAdmin && t.status !== "approved" && (
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button
                      className="primary-btn"
                      style={{ padding: "8px 16px", fontSize: 12 }}
                      onClick={() => onUpdate(t.id, { status: "approved" })}
                    >
                      ✓ Aprovar Documento
                    </button>
                    <button
                      className="action-btn"
                      style={{ width: "auto", padding: "8px 16px", fontSize: 12 }}
                      onClick={() => setSuggInput(p => ({ ...p, [t.id]: p[t.id] === undefined ? "" : undefined }))}
                    >
                      ✏️ Enviar Retorno / Anexar Arquivo
                    </button>
                  </div>
                )}

                {!isAdmin && suggInput[t.id] !== undefined && (
                  <div style={{ marginTop: 14, background: "#F8FAFC", padding: 16, borderRadius: 10, border: "1px solid #E2E8F0" }}>
                    <label className="field-label">Observações para a Dra. Hellen:</label>
                    <textarea
                      className="form-control-input"
                      placeholder="Descreva aqui sua observação ou resposta para a consultoria…"
                      value={suggInput[t.id]}
                      onChange={e => setSuggInput(p => ({ ...p, [t.id]: e.target.value }))}
                      style={{ minHeight: 80, marginBottom: 12 }}
                    />

                    <div style={{ marginBottom: 14 }}>
                      <label className="field-label">Anexar Documento Assinado ou Minuta (Opcional):</label>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                        onChange={e => {
                          if (e.target.files && e.target.files[0]) {
                            const f = e.target.files[0];
                            setSuggFile(p => ({ ...p, [t.id]: f }));
                          }
                        }}
                        style={{ fontSize: 13 }}
                      />
                    </div>

                    <div style={{ display: "flex", gap: 10 }}>
                      <button
                        className="primary-btn"
                        style={{ padding: "8px 18px", fontSize: 12 }}
                        disabled={uploadingTask[t.id]}
                        onClick={async () => {
                          const text = suggInput[t.id] || "";
                          const fileToUpload = suggFile[t.id];
                          setUploadingTask(p => ({ ...p, [t.id]: true }));

                          try {
                            let fileUrl = "";
                            let fileName = "";

                            if (fileToUpload) {
                              const ext = fileToUpload.name.split(".").pop();
                              const fileRef = ref(storage, `retornos_tarefas/${t.id}/${Date.now()}_${uid()}.${ext}`);
                              await uploadBytes(fileRef, fileToUpload);
                              fileUrl = await getDownloadURL(fileRef);
                              fileName = fileToUpload.name;
                            }

                            await onUpdate(t.id, {
                              status: "revision",
                              suggestion: text,
                              returnFile: fileUrl || t.returnFile || "",
                              returnFileName: fileName || t.returnFileName || "",
                              returnData: nowBRFull(),
                            });

                            setSuggInput(p => { const n = { ...p }; delete n[t.id]; return n; });
                            setSuggFile(p => { const n = { ...p }; delete n[t.id]; return n; });
                          } catch (err) {
                            console.error(err);
                            alert("Erro ao enviar retorno. Verifique a conexão com a nuvem.");
                          } finally {
                            setUploadingTask(p => ({ ...p, [t.id]: false }));
                          }
                        }}
                      >
                        {uploadingTask[t.id] ? "Enviando arquivo…" : "Enviar para o Escritório"}
                      </button>
                      <button
                        className="secondary-btn"
                        style={{ padding: "8px 14px", fontSize: 12 }}
                        onClick={() => setSuggInput(p => { const n = { ...p }; delete n[t.id]; return n; })}
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}

                {isAdmin && (
                  <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {t.status !== "approved" && (
                      <button
                        className="primary-btn"
                        style={{ padding: "6px 14px", fontSize: 12 }}
                        onClick={() => onUpdate(t.id, { status: "approved" })}
                      >
                        ✓ Concluir Pendência
                      </button>
                    )}
                    <button
                      className="action-btn action-btn-danger"
                      style={{ width: "auto", display: "inline-flex", padding: "6px 14px", fontSize: 12 }}
                      onClick={() => { if (window.confirm("Remover esta pendência?")) onDelete(t.id); }}
                    >
                      ✕ Remover Pendência
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </main>
  );
}

// ─── PÁGINA DE COMUNICADOS ──────────────────────────────────────────────────
function CommsPage({ comms, schools, user, readComms, markRead, onDelete, onAddClick }) {
  const isAdmin = user.role === "admin";

  return (
    <main className="main-content">
      <div className="page-header-row">
        <div>
          <h1 className="page-title">Comunicados &amp; Avisos Oficiais</h1>
          <p className="page-sub">Orientações da consultoria jurídica educacional</p>
        </div>
        {isAdmin && (
          <button className="primary-btn" onClick={onAddClick}>
            <span>➕</span> Novo Comunicado
          </button>
        )}
      </div>

      {!comms.length ? (
        <div className="empty-box">
          <div className="empty-icon-lg">📢</div>
          <div className="empty-title">Nenhum comunicado publicado</div>
          <p className="empty-desc">Todos os avisos importantes aparecerão nesta central.</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {comms.slice().reverse().map(c => {
            const school = schools.find(s => s.id === c.schoolId);
            const isRead = readComms.includes(c.id);

            return (
              <div
                key={c.id}
                className="stat-card"
                onClick={() => markRead(c.id)}
                style={{ cursor: isRead ? "default" : "pointer", padding: 24 }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14, marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {!isRead && (
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: T.accent, display: "inline-block" }} />
                    )}
                    <h3 style={{ fontFamily: "Syne", fontSize: 17, fontWeight: 700, color: "#0F172A" }}>
                      {c.titulo}
                    </h3>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <button
                      className="whatsapp-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        shareOnWhatsApp(c.titulo, "Comunicado Jurídico", "", c.corpo);
                      }}
                    >
                      💬 WhatsApp
                    </button>
                    <span className={`badge-pill ${c.schoolId === "all" ? "badge-pill-purple" : "badge-pill-blue"}`} style={{ background: c.schoolId === "all" ? T.purpleDim : T.blueDim, color: c.schoolId === "all" ? T.purple : T.blue }}>
                      {c.schoolId === "all" ? "📣 Todos os Colégios" : `🏫 ${school?.name || "Específico"}`}
                    </span>
                  </div>
                </div>

                <p style={{ fontSize: 14, color: "#334155", lineHeight: 1.7, marginBottom: 14, whiteSpace: "pre-wrap" }}>
                  {c.corpo}
                </p>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #F1F5F9", paddingTop: 12 }}>
                  <span style={{ fontSize: 12, color: "#94A3B8", fontFamily: "JetBrains Mono" }}>
                    Publicado em {c.data} por {c.autor}
                  </span>
                  {isAdmin && (
                    <button
                      className="action-btn action-btn-danger"
                      style={{ width: "auto", padding: "4px 12px", fontSize: 11 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm("Remover este comunicado?")) onDelete(c.id);
                      }}
                    >
                      Excluir
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </main>
  );
}

// ─── FORMULÁRIOS ADICIONAIS: TASK & COMM ────────────────────────────────────
function AddTask({ schools, onSave, onCancel }) {
  const [f, setF] = useState({ titulo: "", descricao: "", schoolId: schools[0]?.id || "", deadline: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!f.titulo.trim() || !f.schoolId) { alert("Preencha o título e o colégio destinatário."); return; }
    setSaving(true);
    await onSave({ ...f, id: uid(), status: "pending", data: nowBR() });
  };

  return (
    <main className="main-content">
      <div className="page-header-row">
        <div>
          <button onClick={onCancel} style={{ background: "none", border: "none", color: T.accent, cursor: "pointer", fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
            ← Voltar para pendências
          </button>
          <h1 className="page-title">Criar Pendência para Colégio</h1>
        </div>
      </div>
      <div className="form-panel">
        <form onSubmit={submit}>
          <div className="form-grid-2">
            <div className="form-group-item">
              <label className="field-label">Título da Pendência *</label>
              <input className="form-control-input" value={f.titulo} onChange={e => setF({ ...f, titulo: e.target.value })} placeholder="Ex: Assinatura do Termo Aditivo 2026" autoFocus />
            </div>
            <div className="form-group-item">
              <label className="field-label">Colégio Destinatário *</label>
              <select className="form-control-input" value={f.schoolId} onChange={e => setF({ ...f, schoolId: e.target.value })}>
                {schools.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group-item">
            <label className="field-label">Prazo de Resposta (dd/mm/aaaa)</label>
            <input className="form-control-input" value={f.deadline} onChange={e => setF({ ...f, deadline: e.target.value })} placeholder="Ex: 30/11/2026" />
          </div>
          <div className="form-group-item">
            <label className="field-label">Orientações para a Direção Escolar</label>
            <textarea className="form-control-input" value={f.descricao} onChange={e => setF({ ...f, descricao: e.target.value })} placeholder="Descreva os passos necessários e orientações legais…" />
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <button type="submit" className="primary-btn" disabled={saving}>
              {saving ? "Salvando…" : "Salvar Pendência"}
            </button>
            <button type="button" className="secondary-btn" onClick={onCancel}>Cancelar</button>
          </div>
        </form>
      </div>
    </main>
  );
}

function AddComm({ schools, onSave, onCancel }) {
  const [f, setF] = useState({ titulo: "", corpo: "", schoolId: "all" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!f.titulo.trim() || !f.corpo.trim()) { alert("Preencha título e conteúdo."); return; }
    setSaving(true);
    await onSave({ ...f, id: uid(), data: nowBR(), autor: "Hellen Vieira (Consultoria Jurídica)" });
  };

  return (
    <main className="main-content">
      <div className="page-header-row">
        <div>
          <button onClick={onCancel} style={{ background: "none", border: "none", color: T.accent, cursor: "pointer", fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
            ← Voltar para comunicados
          </button>
          <h1 className="page-title">Publicar Comunicado Oficial</h1>
        </div>
      </div>
      <div className="form-panel">
        <form onSubmit={submit}>
          <div className="form-group-item">
            <label className="field-label">Título do Comunicado *</label>
            <input className="form-control-input" value={f.titulo} onChange={e => setF({ ...f, titulo: e.target.value })} placeholder="Ex: Alterações na Legislação Educacional 2026" autoFocus />
          </div>
          <div className="form-group-item">
            <label className="field-label">Destinatário</label>
            <select className="form-control-input" value={f.schoolId} onChange={e => setF({ ...f, schoolId: e.target.value })}>
              <option value="all">📣 Todos os colégios parceiros</option>
              {schools.map(s => <option key={s.id} value={s.id}>🏫 {s.name}</option>)}
            </select>
          </div>
          <div className="form-group-item">
            <label className="field-label">Conteúdo do Comunicado *</label>
            <textarea className="form-control-input" style={{ minHeight: 140 }} value={f.corpo} onChange={e => setF({ ...f, corpo: e.target.value })} placeholder="Escreva o texto completo do comunicado…" />
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <button type="submit" className="primary-btn" disabled={saving}>
              {saving ? "Publicando…" : "Publicar Comunicado"}
            </button>
            <button type="button" className="secondary-btn" onClick={onCancel}>Cancelar</button>
          </div>
        </form>
      </div>
    </main>
  );
}

// ─── GESTÃO DE COLÉGIOS (COM EXPORTAÇÃO CSV) ─────────────────────────────────
function SchoolList({ schools, docs, tasks, onDelete, onAddClick }) {
  return (
    <main className="main-content">
      <div className="page-header-row">
        <div>
          <h1 className="page-title">Colégios Parceiros</h1>
          <p className="page-sub">{schools.length} colégio{schools.length !== 1 ? "s" : ""} cadastrado{schools.length !== 1 ? "s" : ""}</p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className="secondary-btn" onClick={() => exportReportCSV(schools, docs, tasks)}>
            📥 Exportar Relatório (CSV)
          </button>
          <button className="primary-btn" onClick={onAddClick}>
            <span>➕</span> Cadastrar Novo Colégio
          </button>
        </div>
      </div>

      <div className="cards-grid">
        {schools.map(s => {
          const docCount = docs.filter(d => d.schoolId === s.id || d.schoolId === "all").length;
          const pendCount = tasks.filter(t => t.schoolId === s.id && (t.status === "pending" || t.status === "overdue")).length;

          return (
            <div key={s.id} className="doc-card">
              <div>
                <div className="doc-category-badge">🏫 Instituição</div>
                <h3 className="doc-heading" style={{ fontSize: 18 }}>{s.name}</h3>
                <div style={{ fontSize: 13, color: T.muted, marginBottom: 4 }}>
                  Login de Acesso: <strong>{s.login}</strong>
                </div>
                {s.responsavel && (
                  <div style={{ fontSize: 13, color: T.muted, marginBottom: 4 }}>
                    Responsável: {s.responsavel}
                  </div>
                )}
                {s.telefone && (
                  <div style={{ fontSize: 13, color: T.accentText, fontWeight: 600 }}>
                    WhatsApp: {s.telefone}
                  </div>
                )}
              </div>
              <div style={{ marginTop: 18 }}>
                <div className="doc-footer-meta">
                  <span className="doc-date-text">{docCount} docs · {pendCount} pendência(s)</span>
                  <span className="badge-pill badge-pill-green">Ativo</span>
                </div>
                <div className="doc-actions-row">
                  {s.telefone && (
                    <a
                      href={`https://api.whatsapp.com/send?phone=55${s.telefone.replace(/\D/g, "")}`}
                      target="_blank"
                      rel="noreferrer"
                      className="whatsapp-btn"
                      style={{ flex: 1, justifyContent: "center" }}
                    >
                      💬 Mensagem
                    </a>
                  )}
                  <button
                    className="action-btn action-btn-danger"
                    onClick={() => {
                      if (window.confirm(`Tem certeza que deseja remover o colégio ${s.name}?`)) {
                        onDelete(s.id);
                      }
                    }}
                  >
                    Remover
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </main>
  );
}

function AddSchool({ onSave, onCancel }) {
  const [f, setF] = useState({ name: "", responsavel: "", telefone: "", login: "", senha: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!f.name.trim() || !f.login.trim() || !f.senha) {
      alert("Preencha todos os campos obrigatórios.");
      return;
    }
    setSaving(true);
    await onSave({
      ...f,
      id: uid(),
      login: f.login.trim().toLowerCase().replace(/\s+/g, ""),
      dataCadastro: nowBR()
    });
  };

  return (
    <main className="main-content">
      <div className="page-header-row">
        <div>
          <button onClick={onCancel} style={{ background: "none", border: "none", color: T.accent, cursor: "pointer", fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
            ← Voltar para colégios
          </button>
          <h1 className="page-title">Cadastrar Novo Colégio</h1>
        </div>
      </div>

      <div className="form-panel">
        <form onSubmit={submit}>
          <div className="form-group-item">
            <label className="field-label">Nome do Colégio / Instituição *</label>
            <input className="form-control-input" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="Ex: Colégio Santo Agostinho" autoFocus />
          </div>
          <div className="form-grid-2">
            <div className="form-group-item">
              <label className="field-label">Responsável ou Cargo de Contato</label>
              <input className="form-control-input" value={f.responsavel} onChange={e => setF({ ...f, responsavel: e.target.value })} placeholder="Ex: Dra. Juliana (Diretora)" />
            </div>
            <div className="form-group-item">
              <label className="field-label">WhatsApp do Colégio</label>
              <input className="form-control-input" value={f.telefone} onChange={e => setF({ ...f, telefone: e.target.value })} placeholder="(11) 99999-9999" />
            </div>
          </div>
          <div className="form-grid-2">
            <div className="form-group-item">
              <label className="field-label">Usuário para Acesso *</label>
              <input className="form-control-input" value={f.login} onChange={e => setF({ ...f, login: e.target.value })} placeholder="santoagostinho" />
            </div>
            <div className="form-group-item">
              <label className="field-label">Senha Inicial *</label>
              <input className="form-control-input" type="password" value={f.senha} onChange={e => setF({ ...f, senha: e.target.value })} placeholder="••••••••" />
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
            <button type="submit" className="primary-btn" disabled={saving}>
              {saving ? "Salvando…" : "Cadastrar Colégio"}
            </button>
            <button type="button" className="secondary-btn" onClick={onCancel}>Cancelar</button>
          </div>
        </form>
      </div>
    </main>
  );
}

// ─── ABA MINHA CONTA / PERFIL E CONFIGURAÇÕES ────────────────────────────────
function AccountPage({ user, admins = [], onDeleteAdmin, onUpdateProfile }) {
  const isAdmin = user.role === "admin";
  const [resp, setResp] = useState(user.responsavel || "");
  const [phone, setPhone] = useState(user.telefone || "");
  const [novaSenha, setNovaSenha] = useState("");
  const [novaSenhaConf, setNovaSenhaConf] = useState("");
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e?.preventDefault();
    setMsg("");

    if (novaSenha) {
      if (novaSenha.length < 4) {
        alert("A nova senha deve ter no mínimo 4 caracteres.");
        return;
      }
      if (novaSenha !== novaSenhaConf) {
        alert("As senhas digitadas não coincidem.");
        return;
      }
    }

    setSaving(true);
    const patch = {
      responsavel: resp.trim(),
      telefone: phone.trim(),
    };
    if (novaSenha) {
      patch.senha = novaSenha;
    }

    await onUpdateProfile(patch);
    setSaving(false);
    setMsg("✓ Dados atualizados com sucesso!");
    setNovaSenha("");
    setNovaSenhaConf("");
  };

  return (
    <main className="main-content">
      <div className="page-header-row">
        <div>
          <h1 className="page-title">{isAdmin ? "Painel do Escritório" : "Minha Conta / Perfil"}</h1>
          <p className="page-sub">Informações de acesso e contato</p>
        </div>
      </div>

      <div className="form-panel">
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24, paddingBottom: 20, borderBottom: "1px solid #E2E8F0" }}>
          <div className="user-avatar" style={{ width: 56, height: 56, fontSize: 20 }}>
            {user.name.slice(0, 2).toUpperCase()}
          </div>
          <div>
            <h3 style={{ fontFamily: "Syne", fontSize: 18, fontWeight: 700, color: "#0F172A" }}>{user.name}</h3>
            <div style={{ fontSize: 13, color: T.muted }}>
              {isAdmin ? "Perfil: Administrador Geral (Escritório Vieira & Melul)" : `Login: ${user.login} · Cadastrado em ${user.dataCadastro || "Recentemente"}`}
            </div>
          </div>
        </div>

        {isAdmin ? (
          <div>
            <div style={{ background: "#F8FAFC", padding: 20, borderRadius: 12, border: "1px solid #E2E8F0", marginBottom: 24 }}>
              <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: "#1E293B" }}>
                🔒 Perfil de Gestão — Equipe Vieira &amp; Melul
              </h4>
              <p style={{ fontSize: 13, color: "#475569", lineHeight: 1.6 }}>
                Você tem permissão total para publicar documentos, criar pendências, enviar comunicados e cadastrar colégios parceiros.
              </p>
              <div style={{ marginTop: 12, fontSize: 12, color: T.muted }}>
                Usuário conectado: <strong>{user.login}</strong> ({user.name})
              </div>
            </div>

            <div style={{ borderTop: "1px solid #E2E8F0", paddingTop: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <h4 style={{ fontFamily: "Syne", fontSize: 16, fontWeight: 700, color: "#0F172A" }}>
                  👥 Administradores e Consultores Cadastrados
                </h4>
                <span className="badge-pill badge-pill-green">
                  Acesso Restrito
                </span>
              </div>

              {admins && admins.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {admins.map(adm => (
                    <div key={adm.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#FFFFFF", padding: "12px 16px", borderRadius: 10, border: "1px solid #E2E8F0" }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: "#1E293B" }}>{adm.name}</div>
                        <div style={{ fontSize: 12, color: T.muted }}>
                          Login: <strong>{adm.login}</strong> · {adm.cargo || "Advogado(a)"} · Criado em {adm.dataCadastro || "Recentemente"}
                        </div>
                      </div>
                      {adm.id !== user.id && (
                        <button
                          className="action-btn action-btn-danger"
                          style={{ width: "auto", padding: "6px 14px", fontSize: 12 }}
                          onClick={() => {
                            if (window.confirm(`Remover o acesso de administrador de ${adm.name}?`)) {
                              onDeleteAdmin(adm.id);
                            }
                          }}
                        >
                          Remover Acesso
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: T.muted, background: "#F8FAFC", padding: 16, borderRadius: 10 }}>
                  Apenas os usuários mestres (hellen / admin) estão pré-configurados. Novos administradores cadastrados na tela inicial aparecerão nesta lista.
                </div>
              )}
            </div>
          </div>
        ) : (
          <form onSubmit={handleSave}>
            <div className="form-grid-2">
              <div className="form-group-item">
                <label className="field-label">Responsável / Cargo de Contato</label>
                <input
                  className="form-control-input"
                  value={resp}
                  onChange={e => setResp(e.target.value)}
                  placeholder="Ex: Dra. Mariana (Diretora)"
                />
              </div>

              <div className="form-group-item">
                <label className="field-label">WhatsApp para Notificações</label>
                <input
                  className="form-control-input"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="(11) 98765-4321"
                />
              </div>
            </div>

            <div style={{ borderTop: "1px solid #E2E8F0", paddingTop: 20, marginTop: 10 }}>
              <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 14, color: "#1E293B" }}>
                Alterar Senha do Colégio
              </h4>

              <div className="form-grid-2">
                <div className="form-group-item">
                  <label className="field-label">Nova Senha</label>
                  <input
                    className="form-control-input"
                    type="password"
                    value={novaSenha}
                    onChange={e => setNovaSenha(e.target.value)}
                    placeholder="Deixe em branco para manter a atual"
                  />
                </div>
                <div className="form-group-item">
                  <label className="field-label">Confirmar Nova Senha</label>
                  <input
                    className="form-control-input"
                    type="password"
                    value={novaSenhaConf}
                    onChange={e => setNovaSenhaConf(e.target.value)}
                    placeholder="Repita a nova senha"
                  />
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: 12, marginTop: 14 }}>
              <button type="submit" className="primary-btn" disabled={saving}>
                {saving ? "Salvando…" : "Salvar Alterações"}
              </button>
            </div>

            {msg && <div className="login-success" style={{ textAlign: "left", marginTop: 16 }}>{msg}</div>}
          </form>
        )}
      </div>
    </main>
  );
}
